/**
 * @page ProzessDetailPage
 * @route /prozesse/:id
 * @description Prozess-Detail mit integriertem Editor — 70/30 Split-Layout.
 *   Tabs: Schritte, KPIs, Medien, Kommentare, Versionen.
 *   Sidebar: Status-Workflow, Details, Verknuepfungen, Tags, Changelog.
 * @roles admin, ops_manager
 * @i18n prozesse.detail.*
 * @endpoints GET/PUT /api/prozesse/:id, plus sub-resource endpoints
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import BackLink from '../components/BackLink';
import MediaLightbox from '../components/MediaLightbox';
import SchrittMediaSection from '../components/SchrittMediaSection';
import {
  ChevronRight, GripVertical, Plus, Trash2, Pencil, Check, X as XIcon, Upload,
  FileText, Image, Video, Download, Send, Clock, User, Link2, Tag,
  AlertTriangle, Shield, CheckCircle2, Archive, Eye, ArrowLeft,
  Activity, Target, Settings, MessageSquare, History, Loader2, Sparkles,
  GitBranch, ZoomIn, ZoomOut, RotateCcw, QrCode, MapPin, Copy,
  Play, Volume2, Mic, Lock, Unlock, ShieldCheck, FileEdit, GitCompare, Award,
  ChevronDown, MoreHorizontal
} from 'lucide-react';
import MCTestEditor from '../components/prozess/MCTestEditor';
import ProcessRichTextEditor from '../components/ProcessRichTextEditor';
import ProcessHtmlView from '../components/ProcessHtmlView';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_COLORS = {
  entwurf: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400', labelKey: 'prozesse.lifecycle.statusEntwurf', label: 'Entwurf', icon: FileEdit },
  in_review: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', labelKey: 'prozesse.lifecycle.statusInReview', label: 'In Review', icon: Eye },
  aktiv: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', labelKey: 'prozesse.lifecycle.statusGesperrt', label: 'Gesperrt', icon: Lock },
  entsperrt: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', labelKey: 'prozesse.lifecycle.statusEntsperrt', label: 'Entsperrt', icon: Unlock },
  archiviert: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400', labelKey: 'prozesse.lifecycle.statusArchiviert', label: 'Archiviert', icon: Archive },
};

const EDITABLE_STATES = new Set(['entwurf', 'entsperrt']);

function buildVersionString(p) {
  if (!p) return '1.0.0';
  return `${p.aktuelle_version || 1}.${p.version_minor || 0}.${p.version_patch || 0}`;
}

const LIFECYCLE_AKTION_META = {
  erstellt: { labelKey: 'prozesse.lifecycle.actErstellt', label: 'Erstellt', icon: Plus, color: '#338edc' },
  review_angefordert: { labelKey: 'prozesse.lifecycle.actReview', label: 'Review angefordert', icon: Eye, color: '#F59E0B' },
  freigegeben: { labelKey: 'prozesse.lifecycle.actFreigegeben', label: 'Freigegeben', icon: CheckCircle2, color: '#10B981' },
  abgelehnt: { labelKey: 'prozesse.lifecycle.actAbgelehnt', label: 'Abgelehnt', icon: XIcon, color: '#EF4444' },
  archiviert: { labelKey: 'prozesse.lifecycle.actArchiviert', label: 'Archiviert', icon: Archive, color: '#6B7280' },
  entsperrt: { labelKey: 'prozesse.lifecycle.actEntsperrt', label: 'Entsperrt', icon: Unlock, color: '#338edc' },
  gesperrt: { labelKey: 'prozesse.lifecycle.actGesperrt', label: 'Wieder gesperrt', icon: Lock, color: '#10B981' },
  genehmiger_gesetzt: { labelKey: 'prozesse.lifecycle.actGenehmigerSet', label: 'Genehmiger gesetzt', icon: ShieldCheck, color: '#6366F1' },
  genehmiger_entfernt: { labelKey: 'prozesse.lifecycle.actGenehmigerCleared', label: 'Genehmiger entfernt', icon: Shield, color: '#9CA3AF' },
  version_bump_major: { labelKey: 'prozesse.lifecycle.actVersionMajor', label: 'Major-Version', icon: GitBranch, color: '#EF4444' },
  version_bump_minor: { labelKey: 'prozesse.lifecycle.actVersionMinor', label: 'Minor-Version', icon: GitBranch, color: '#F59E0B' },
  version_bump_patch: { labelKey: 'prozesse.lifecycle.actVersionPatch', label: 'Patch-Version', icon: GitBranch, color: '#10B981' },
  version_erstellt: { labelKey: 'prozesse.lifecycle.actVersionErstellt', label: 'Version-Snapshot', icon: History, color: '#6B7280' },
  aenderung_vorgeschlagen: { labelKey: 'prozesse.lifecycle.actAenderungVorgeschlagen', label: 'Änderung vorgeschlagen', icon: GitBranch, color: '#6366F1' },
  aenderung_eingereicht: { labelKey: 'prozesse.lifecycle.actAenderungEingereicht', label: 'Änderung eingereicht', icon: Eye, color: '#F59E0B' },
  aenderung_genehmigt: { labelKey: 'prozesse.lifecycle.actAenderungGenehmigt', label: 'Änderung genehmigt', icon: CheckCircle2, color: '#10B981' },
  aenderung_abgelehnt: { labelKey: 'prozesse.lifecycle.actAenderungAbgelehnt', label: 'Änderung abgelehnt', icon: XIcon, color: '#EF4444' },
  genehmigt: { labelKey: 'prozesse.lifecycle.actGenehmigt', label: 'Genehmigt', icon: CheckCircle2, color: '#10B981' },
  betroffene_benachrichtigt: { labelKey: 'prozesse.lifecycle.actBetroffeneBenachrichtigt', label: 'Betroffene benachrichtigt', icon: MessageSquare, color: '#338edc' },
  schulung_zugewiesen: { labelKey: 'prozesse.lifecycle.actSchulungZugewiesen', label: 'Schulung zugewiesen', icon: Target, color: '#6366F1' },
};

const TYP_LABELS = { standard: 'Standard', kundenspezifisch: 'Kundenspezifisch', ausnahme: 'Ausnahme' };
const TYP_COLORS = { standard: '#338edc', kundenspezifisch: '#6366F1', ausnahme: '#F59E0B' };
const RISIKO_COLORS = { low: '#10B981', medium: '#F59E0B', high: '#EF4444' };
const RISIKO_LABELS = { low: 'Niedrig', medium: 'Mittel', high: 'Hoch' };
const SCHRITT_TYP_COLORS = { aktion: '#338edc', entscheidung: '#F59E0B', warten: '#9CA3AF', pruefung: '#10B981' };
const KPI_TYP_COLORS = { operativ: '#338edc', kunden: '#6366F1', wirtschaftlich: '#F59E0B' };
const VERKN_TYP_LABELS = { kunde: 'Kunde', system: 'System', prozess: 'Prozess' };

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// --- Badge Component ---
function Badge({ label, color, className = '' }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}
      style={{ backgroundColor: color + '18', color }}
    >
      {label}
    </span>
  );
}

// --- StatusBadge Component ---
function StatusBadge({ status, large = false, t }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.entwurf;
  const Icon = s.icon;
  const label = t ? (t(s.labelKey) || s.label) : s.label;
  return (
    <span className={`inline-flex items-center gap-1.5 ${large ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-0.5 text-xs'} rounded-full font-medium ${s.bg} ${s.text}`}>
      {Icon ? <Icon className={large ? 'w-3.5 h-3.5' : 'w-3 h-3'} strokeWidth={2} /> : <span className={`w-2 h-2 rounded-full ${s.dot}`} />}
      {label}
    </span>
  );
}

// --- Confirm Dialog ---
function ConfirmDialog({ open, title, message, onConfirm, onCancel, children, confirmLabel = 'Bestaetigen', confirmColor = '#EF4444' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onCancel}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">{title}</h3>
        {message && <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-4">{message}</p>}
        {children}
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
            Abbrechen
          </button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm rounded-lg text-white font-medium" style={{ backgroundColor: confirmColor }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- StepCard Component ---
function StepCard({ step, index, editing, onEdit, onSave, onCancel, onDelete, dragHandlers, prozessId, onMediaChanged, onOpenLightbox, t }) {
  const [form, setForm] = useState({ ...step });
  useEffect(() => { setForm({ ...step }); }, [step]);

  const typColor = SCHRITT_TYP_COLORS[step.schritt_typ] || '#9CA3AF';
  const medien = step.medien || [];

  if (editing) {
    return (
      <div className="rounded-xl border border-[#338edc] bg-[var(--bg-card,#fff)] p-4 mb-3">
        <div className="space-y-3">
          <input value={form.titel || ''} onChange={e => setForm({ ...form, titel: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] text-sm focus:outline-none focus:border-[#338edc]"
            placeholder="Schritt-Titel" />
          <ProcessRichTextEditor
            value={form.beschreibung || ''}
            onChange={(html) => setForm({ ...form, beschreibung: html })}
            prozessId={prozessId}
            placeholder={t('prozesse.detail.descPlaceholder') || 'Beschreibung'}
            minHeight={140}
          />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.verantwortlich || ''} onChange={e => setForm({ ...form, verantwortlich: e.target.value })}
              className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]"
              placeholder="Verantwortlich" />
            <input value={form.system_verknuepfung || ''} onChange={e => setForm({ ...form, system_verknuepfung: e.target.value })}
              className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]"
              placeholder="System-Verknuepfung" />
            <input type="number" step="0.1" value={form.dauer_soll ?? ''} onChange={e => setForm({ ...form, dauer_soll: e.target.value })}
              className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]"
              placeholder="Dauer (min)" />
            <select value={form.schritt_typ || 'aktion'} onChange={e => setForm({ ...form, schritt_typ: e.target.value })}
              className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]">
              <option value="aktion">Aktion</option>
              <option value="entscheidung">Entscheidung</option>
              <option value="warten">Warten</option>
              <option value="pruefung">Pruefung</option>
            </select>
          </div>
          <SchrittMediaSection
            prozessId={prozessId}
            schrittId={step.id}
            medien={medien}
            editable
            onChanged={onMediaChanged}
            onOpenLightbox={onOpenLightbox}
            t={t}
          />
          <div className="flex justify-end gap-2">
            <button onClick={onCancel} className="p-2 rounded-lg hover:bg-[var(--bg-hover,#FAFAFE)]"><XIcon className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" /></button>
            <button onClick={() => onSave(form)} className="p-2 rounded-lg hover:bg-[var(--bg-hover,#FAFAFE)]"><Check className="w-4 h-4 text-[#338edc]" /></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4 mb-3 hover:border-[#338edc]/30 transition-colors"
      draggable
      data-step-index={index}
      {...dragHandlers}
    >
      <div className="flex items-start gap-3">
        <div className="flex items-center gap-2 pt-0.5 cursor-grab active:cursor-grabbing">
          <GripVertical className="w-4 h-4 text-[var(--text-tertiary,#6B7280)] opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
          <span className="w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold text-white" style={{ backgroundColor: typColor }}>
            {index + 1}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-[var(--text-primary,#1A1A2E)]">{step.titel}</span>
            <Badge label={step.schritt_typ} color={typColor} />
          </div>
          {step.beschreibung && (
            <ProcessHtmlView html={step.beschreibung} className="text-sm mb-2" />
          )}
          <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-tertiary,#6B7280)]">
            {step.verantwortlich && (
              <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" strokeWidth={1.5} />{step.verantwortlich}</span>
            )}
            {step.system_verknuepfung && (
              <span className="flex items-center gap-1"><Settings className="w-3.5 h-3.5" strokeWidth={1.5} />{step.system_verknuepfung}</span>
            )}
            {step.dauer_soll != null && (
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} />{step.dauer_soll} min</span>
            )}
          </div>
          {medien.length > 0 && (
            <div className="mt-3">
              <SchrittMediaSection
                prozessId={prozessId}
                schrittId={step.id}
                medien={medien}
                editable={false}
                onOpenLightbox={onOpenLightbox}
                t={t}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-[var(--bg-hover,#FAFAFE)]"><Pencil className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} /></button>
          <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-400" strokeWidth={1.5} /></button>
        </div>
      </div>
    </div>
  );
}

// --- KpiCard Component ---
function KpiCard({ kpi, onEdit, onDelete, messung }) {
  const typColor = KPI_TYP_COLORS[kpi.kpi_typ] || '#338edc';
  const ampelColor = messung?.status === 'gruen' ? '#10B981' : messung?.status === 'gelb' ? '#F59E0B' : messung?.status === 'rot' ? '#EF4444' : null;
  return (
    <div className="group rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4 mb-3">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4" style={{ color: typColor }} strokeWidth={1.5} />
          <span className="font-medium text-[var(--text-primary,#1A1A2E)]">{kpi.name}</span>
          <Badge label={kpi.kpi_typ} color={typColor} />
        </div>
        <div className="flex items-center gap-2">
          {ampelColor && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: ampelColor + '18', color: ampelColor }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ampelColor }} />
              {messung.messwert} {kpi.einheit}
            </span>
          )}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-[var(--bg-hover,#FAFAFE)]"><Pencil className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} /></button>
            <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-400" strokeWidth={1.5} /></button>
          </div>
        </div>
      </div>
      {kpi.beschreibung && <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-3">{kpi.beschreibung}</p>}
      <div className="flex flex-wrap gap-4 text-sm">
        <div>
          <span className="text-[var(--text-tertiary,#6B7280)]">Ziel: </span>
          <span className="font-semibold text-[var(--text-primary,#1A1A2E)]">{kpi.zielwert} {kpi.einheit}</span>
        </div>
        {kpi.schwelle_gruen != null && (
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[var(--text-tertiary,#6B7280)]">{'>='} {kpi.schwelle_gruen}</span>
          </div>
        )}
        {kpi.schwelle_gelb != null && (
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-[var(--text-tertiary,#6B7280)]">{'>='} {kpi.schwelle_gelb}</span>
          </div>
        )}
        {kpi.datenquelle && (
          <div className="text-[var(--text-tertiary,#6B7280)]">Quelle: {kpi.datenquelle}</div>
        )}
      </div>
      {messung?.gemessen_am && (
        <p className="text-[10px] text-[var(--text-muted,#9CA3AF)] mt-2">
          Gemessen: {new Date(messung.gemessen_am).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          {messung.quelle && ` — ${messung.quelle}`}
        </p>
      )}
    </div>
  );
}

// --- KpiForm Component ---
function KpiForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || { name: '', kpi_typ: 'operativ', beschreibung: '', zielwert: '', einheit: '', datenquelle: '', schwelle_gruen: '', schwelle_gelb: '' });
  return (
    <div className="rounded-xl border border-[#338edc] bg-[var(--bg-card,#fff)] p-4 mb-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="KPI Name"
          className="col-span-2 px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc]" />
        <select value={form.kpi_typ} onChange={e => setForm({ ...form, kpi_typ: e.target.value })}
          className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]">
          <option value="operativ">Operativ</option>
          <option value="kunden">Kunden</option>
          <option value="wirtschaftlich">Wirtschaftlich</option>
        </select>
        <input value={form.einheit} onChange={e => setForm({ ...form, einheit: e.target.value })} placeholder="Einheit (z.B. %, min)"
          className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
      </div>
      <textarea value={form.beschreibung} onChange={e => setForm({ ...form, beschreibung: e.target.value })} rows={2} placeholder="Beschreibung"
        className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm resize-none focus:outline-none focus:border-[#338edc]" />
      <div className="grid grid-cols-3 gap-3">
        <input type="number" step="0.01" value={form.zielwert ?? ''} onChange={e => setForm({ ...form, zielwert: e.target.value })} placeholder="Zielwert"
          className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
        <input type="number" step="0.01" value={form.schwelle_gruen ?? ''} onChange={e => setForm({ ...form, schwelle_gruen: e.target.value })} placeholder="Schwelle Gruen"
          className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
        <input type="number" step="0.01" value={form.schwelle_gelb ?? ''} onChange={e => setForm({ ...form, schwelle_gelb: e.target.value })} placeholder="Schwelle Gelb"
          className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
      </div>
      <input value={form.datenquelle || ''} onChange={e => setForm({ ...form, datenquelle: e.target.value })} placeholder="Datenquelle"
        className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">Abbrechen</button>
        <button onClick={() => onSave(form)} disabled={!form.name} className="px-3 py-1.5 text-sm rounded-lg text-white font-medium bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-40">Speichern</button>
      </div>
    </div>
  );
}

// --- VersionModal ---
function VersionModal({ open, onClose, versionData }) {
  if (!open || !versionData) return null;
  const inhalt = versionData.inhalt || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)]">
            Version {versionData.version}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-hover,#FAFAFE)]"><XIcon className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>
        <p className="text-sm text-[var(--text-tertiary,#6B7280)] mb-4">
          {formatDate(versionData.erstellt_am)} — {versionData.erstellt_von_name}
        </p>
        {versionData.aenderungsgrund && (
          <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-4 italic">"{versionData.aenderungsgrund}"</p>
        )}
        {inhalt.schritte?.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium text-[var(--text-primary,#1A1A2E)] mb-2">Schritte</h4>
            {inhalt.schritte.map((s, i) => (
              <div key={i} className="flex items-start gap-2 mb-2 text-sm">
                <span className="w-6 h-6 flex items-center justify-center rounded-full text-xs text-white bg-[#338edc] shrink-0">{i + 1}</span>
                <div>
                  <span className="font-medium text-[var(--text-primary,#1A1A2E)]">{s.titel}</span>
                  {s.beschreibung && <ProcessHtmlView html={s.beschreibung} className="text-sm" />}
                </div>
              </div>
            ))}
          </div>
        )}
        {inhalt.kpis?.length > 0 && (
          <div>
            <h4 className="font-medium text-[var(--text-primary,#1A1A2E)] mb-2">KPIs</h4>
            {inhalt.kpis.map((k, i) => (
              <div key={i} className="text-sm mb-1">
                <span className="font-medium text-[var(--text-primary,#1A1A2E)]">{k.name}</span>
                <span className="text-[var(--text-tertiary,#6B7280)]"> — Ziel: {k.zielwert} {k.einheit}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// --- ProzessFlowchart Component ---
const FC_NODE_W = 280;
const FC_NODE_H = 80;
const FC_DIAMOND_SIZE = 90;
const FC_GAP = 56;
const FC_PILL_W = 160;
const FC_PILL_H = 40;
const FC_CANVAS_W = 680;

const FC_TYPE_STYLES = {
  aktion:       { fill: '#EBF4FD', stroke: '#93C5FD', textColor: '#1E40AF' },
  entscheidung: { fill: '#FEF3C7', stroke: '#FCD34D', textColor: '#92400E' },
  pruefung:     { fill: '#D1FAE5', stroke: '#6EE7B7', textColor: '#065F46' },
  warten:       { fill: '#F3F4F6', stroke: '#D1D5DB', textColor: '#4B5563', dash: '6,3' },
};

function flowLayout(schritte) {
  const nodes = [];
  let y = 0;

  // Start pill
  nodes.push({ type: 'start', x: FC_CANVAS_W / 2 - FC_PILL_W / 2, y, w: FC_PILL_W, h: FC_PILL_H });
  y += FC_PILL_H + FC_GAP;

  for (let i = 0; i < schritte.length; i++) {
    const s = schritte[i];
    const isDiamond = s.schritt_typ === 'entscheidung';
    const w = isDiamond ? FC_DIAMOND_SIZE : FC_NODE_W;
    const h = isDiamond ? FC_DIAMOND_SIZE : FC_NODE_H;
    nodes.push({
      type: 'step',
      step: s,
      index: i,
      x: FC_CANVAS_W / 2 - w / 2,
      y,
      w,
      h,
      isDiamond,
    });
    y += h + FC_GAP;
  }

  // End pill
  nodes.push({ type: 'end', x: FC_CANVAS_W / 2 - FC_PILL_W / 2, y, w: FC_PILL_W, h: FC_PILL_H });
  y += FC_PILL_H + 20;

  return { nodes, totalHeight: y };
}

function FlowNode({ node, hovered, onHover, onLeave, onClick }) {
  if (node.type === 'start' || node.type === 'end') {
    return (
      <g>
        <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={node.h / 2} ry={node.h / 2}
          fill="var(--bg-inset,#F3F4F6)" stroke="var(--border-card,#D1D5DB)" strokeWidth={1.5} />
        <text x={node.x + node.w / 2} y={node.y + node.h / 2 + 1} textAnchor="middle" dominantBaseline="middle"
          fill="var(--text-tertiary,#6B7280)" fontSize={13} fontWeight={500} fontFamily="DM Sans, sans-serif">
          {node.type === 'start' ? 'Start' : 'Ende'}
        </text>
      </g>
    );
  }

  const s = node.step;
  const style = FC_TYPE_STYLES[s.schritt_typ] || FC_TYPE_STYLES.aktion;
  const isHov = hovered === node.index;

  if (node.isDiamond) {
    const cx = node.x + node.w / 2;
    const cy = node.y + node.h / 2;
    const half = node.w / 2;
    const points = `${cx},${cy - half} ${cx + half},${cy} ${cx},${cy + half} ${cx - half},${cy}`;
    return (
      <g onMouseEnter={() => onHover(node.index)} onMouseLeave={onLeave} onClick={() => onClick(node.index)}
        style={{ cursor: 'pointer' }}>
        <polygon points={points} fill={style.fill} stroke={style.stroke} strokeWidth={isHov ? 2.5 : 1.5} />
        <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="middle"
          fill={style.textColor} fontSize={11} fontWeight={600} fontFamily="DM Sans, sans-serif">
          {node.index + 1}
        </text>
        <text x={cx} y={cy + 10} textAnchor="middle" dominantBaseline="middle"
          fill={style.textColor} fontSize={10} fontWeight={500} fontFamily="DM Sans, sans-serif">
          {s.titel?.length > 16 ? s.titel.slice(0, 15) + '…' : s.titel}
        </text>
      </g>
    );
  }

  // Regular rectangle node
  return (
    <g onMouseEnter={() => onHover(node.index)} onMouseLeave={onLeave} onClick={() => onClick(node.index)}
      style={{ cursor: 'pointer' }}>
      <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={10} ry={10}
        fill={style.fill} stroke={isHov ? '#338edc' : style.stroke} strokeWidth={isHov ? 2.5 : 1.5}
        strokeDasharray={style.dash || 'none'} />
      {/* Number circle */}
      <circle cx={node.x + 22} cy={node.y + 24} r={12} fill={style.stroke} />
      <text x={node.x + 22} y={node.y + 25} textAnchor="middle" dominantBaseline="middle"
        fill="#fff" fontSize={11} fontWeight={700} fontFamily="DM Sans, sans-serif">{node.index + 1}</text>
      {/* Title */}
      <text x={node.x + 42} y={node.y + 26} fill="var(--text-primary,#1A1A2E)" fontSize={13} fontWeight={500}
        fontFamily="DM Sans, sans-serif">
        {s.titel?.length > 28 ? s.titel.slice(0, 27) + '…' : s.titel}
      </text>
      {/* Sub-info line */}
      <text x={node.x + 22} y={node.y + 50} fill="var(--text-tertiary,#6B7280)" fontSize={11}
        fontFamily="DM Sans, sans-serif">
        {[
          s.dauer_soll != null ? `⏱ ${s.dauer_soll} min` : null,
          s.verantwortlich || null,
          s.system_verknuepfung ? `⚙ ${s.system_verknuepfung}` : null,
        ].filter(Boolean).join('  ·  ')}
      </text>
      {/* Pruefung check icon */}
      {s.schritt_typ === 'pruefung' && (
        <text x={node.x + node.w - 20} y={node.y + 24} textAnchor="middle" dominantBaseline="middle"
          fill="#10B981" fontSize={16}>✓</text>
      )}
    </g>
  );
}

function FlowArrow({ from, to, label }) {
  const x1 = from.x + from.w / 2;
  const y1 = from.y + from.h;
  const x2 = to.x + to.w / 2;
  const y2 = to.y;
  const midY = (y1 + y2) / 2;

  if (Math.abs(x1 - x2) < 2) {
    // Straight arrow
    return (
      <g>
        <line x1={x1} y1={y1} x2={x2} y2={y2 - 6} stroke="var(--text-tertiary,#9CA3AF)" strokeWidth={1.5} markerEnd="url(#arrowhead)" />
        {label && (
          <text x={x1 + 8} y={midY + 3} fill="var(--text-tertiary,#6B7280)" fontSize={10} fontFamily="DM Sans, sans-serif">{label}</text>
        )}
      </g>
    );
  }

  // Curved arrow (for side branches)
  return (
    <g>
      <path d={`M${x1},${y1} C${x1},${midY} ${x2},${midY} ${x2},${y2 - 6}`}
        fill="none" stroke="var(--text-tertiary,#9CA3AF)" strokeWidth={1.5} markerEnd="url(#arrowhead)" />
      {label && (
        <text x={(x1 + x2) / 2} y={midY - 4} fill="var(--text-tertiary,#6B7280)" fontSize={10}
          textAnchor="middle" fontFamily="DM Sans, sans-serif">{label}</text>
      )}
    </g>
  );
}

// #1472 Etappe B': Schritt-Beschreibung ist jetzt HTML — fuer den kompakten
// Flowchart-Tooltip auf Plaintext reduzieren (Tags raus, Basis-Entities zurueck).
function htmlToPlain(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function FlowTooltip({ node, containerRef }) {
  if (!node?.step?.beschreibung) return null;
  const tipW = 240;
  const left = Math.min(node.x + node.w / 2 - tipW / 2, FC_CANVAS_W - tipW - 8);
  return (
    <div
      className="absolute pointer-events-none rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] px-3 py-2 text-xs text-[var(--text-secondary,#4B5563)] shadow-sm z-10"
      style={{
        left: `${Math.max(8, left)}px`,
        top: `${node.y + node.h + 8}px`,
        width: tipW,
        maxWidth: tipW,
      }}
    >
      <p className="font-medium text-[var(--text-primary,#1A1A2E)] mb-0.5">{node.step.titel}</p>
      <p className="whitespace-pre-line">{htmlToPlain(node.step.beschreibung)}</p>
    </div>
  );
}

function ProzessFlowchart({ schritte, versionen, t, onClickStep }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const [hovered, setHovered] = useState(null);

  // Version compare
  const [compareVersion, setCompareVersion] = useState(null);
  const [compareSchritte, setCompareSchritte] = useState(null);

  const { nodes, totalHeight } = flowLayout(schritte);

  const hoveredNode = hovered !== null ? nodes.find(n => n.type === 'step' && n.index === hovered) : null;

  // Pan handlers
  const handleMouseDown = (e) => {
    if (e.target.tagName === 'svg' || e.target.tagName === 'rect' && e.target.getAttribute('data-bg') === 'true') {
      setDragging(true);
      dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  };
  const handleMouseMove = (e) => {
    if (!dragging || !dragStart.current) return;
    setPan({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  };
  const handleMouseUp = () => { setDragging(false); dragStart.current = null; };

  // Wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(z => Math.max(0.3, Math.min(2.5, z + delta)));
  };

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Export PNG
  const exportPng = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.setAttribute('width', FC_CANVAS_W * 2);
    clone.setAttribute('height', totalHeight * 2);
    const data = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = FC_CANVAS_W * 2;
      canvas.height = totalHeight * 2;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      const a = document.createElement('a');
      a.download = 'prozess-flowchart.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = url;
  };

  // Export SVG
  const exportSvg = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.setAttribute('width', FC_CANVAS_W);
    clone.setAttribute('height', totalHeight);
    const data = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const a = document.createElement('a');
    a.download = 'prozess-flowchart.svg';
    a.href = URL.createObjectURL(blob);
    a.click();
  };

  // Load compare version
  const loadCompare = async (version) => {
    if (!version) { setCompareVersion(null); setCompareSchritte(null); return; }
    try {
      const v = versionen.find(vv => vv.version === parseInt(version));
      if (v?.inhalt) {
        const inhalt = typeof v.inhalt === 'string' ? JSON.parse(v.inhalt) : v.inhalt;
        setCompareSchritte(inhalt.schritte || []);
        setCompareVersion(parseInt(version));
      }
    } catch { /* ignore */ }
  };

  // Diff detection for compare mode
  const getDiffType = (stepTitle, otherSchritte) => {
    if (!otherSchritte) return null;
    const match = otherSchritte.find(s => s.titel === stepTitle);
    if (!match) return 'added';
    return null;
  };

  const renderFlowSvg = (flowSchritte, diffSource = null) => {
    const layout = flowLayout(flowSchritte);
    return (
      <svg ref={!diffSource ? svgRef : undefined} viewBox={`0 0 ${FC_CANVAS_W} ${layout.totalHeight}`}
        width="100%" style={{ maxHeight: compareVersion ? 600 : undefined }}>
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="var(--text-tertiary,#9CA3AF)" />
          </marker>
        </defs>
        {/* Arrows */}
        {layout.nodes.map((node, i) => {
          if (i === 0) return null;
          const prev = layout.nodes[i - 1];
          return <FlowArrow key={`a-${i}`} from={prev} to={node} />;
        })}
        {/* Nodes */}
        {layout.nodes.map((node, i) => {
          const diff = node.type === 'step' && diffSource ? getDiffType(node.step.titel, diffSource) : null;
          return (
            <g key={`n-${i}`}>
              {diff && (
                <rect x={node.x - 4} y={node.y - 4} width={node.w + 8} height={node.h + 8} rx={14}
                  fill="none"
                  stroke={diff === 'added' ? '#10B981' : diff === 'removed' ? '#EF4444' : '#F59E0B'}
                  strokeWidth={2.5}
                  strokeDasharray={diff === 'removed' ? '6,3' : 'none'} />
              )}
              <FlowNode node={node} hovered={!diffSource ? hovered : null}
                onHover={!diffSource ? setHovered : () => {}}
                onLeave={!diffSource ? () => setHovered(null) : () => {}}
                onClick={!diffSource ? onClickStep : () => {}} />
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.min(2.5, z + 0.2))}
            className="p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-hover,#FAFAFE)]"
            title="Zoom in">
            <ZoomIn className="w-4 h-4 text-[var(--text-secondary,#4B5563)]" strokeWidth={1.5} />
          </button>
          <span className="text-xs text-[var(--text-tertiary,#6B7280)] w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.max(0.3, z - 0.2))}
            className="p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-hover,#FAFAFE)]"
            title="Zoom out">
            <ZoomOut className="w-4 h-4 text-[var(--text-secondary,#4B5563)]" strokeWidth={1.5} />
          </button>
          <button onClick={resetView}
            className="p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-hover,#FAFAFE)]"
            title="Reset">
            <RotateCcw className="w-4 h-4 text-[var(--text-secondary,#4B5563)]" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* Version compare */}
          {versionen?.length > 1 && (
            <select
              value={compareVersion || ''}
              onChange={e => loadCompare(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-xs text-[var(--text-primary,#1A1A2E)]"
            >
              <option value="">{t('prozesse.flowchart.compare') || 'Mit Version vergleichen'}</option>
              {(versionen || []).filter(v => v.version < (versionen[0]?.version || 999)).map(v => (
                <option key={v.version} value={v.version}>v{v.version_label || v.version}</option>
              ))}
            </select>
          )}
          <button onClick={exportSvg}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] text-xs font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> SVG
          </button>
          <button onClick={exportPng}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] text-xs font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> PNG
          </button>
        </div>
      </div>

      {/* Compare legend */}
      {compareVersion && (
        <div className="flex items-center gap-4 mb-3 text-xs text-[var(--text-tertiary,#6B7280)]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-emerald-500" /> {t('prozesse.flowchart.added') || 'Hinzugefügt'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-red-400 border-dashed" /> {t('prozesse.flowchart.removed') || 'Entfernt'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-amber-400" /> {t('prozesse.flowchart.changed') || 'Geändert'}</span>
        </div>
      )}

      {/* Flowchart area */}
      {!compareVersion ? (
        <div
          ref={containerRef}
          className="relative rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden"
          style={{ minHeight: 300 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <div style={{
            transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
            transformOrigin: 'top center',
            transition: dragging ? 'none' : 'transform 0.15s ease',
            padding: '16px 0',
          }}>
            {schritte.length === 0 ? (
              <div className="text-center py-16 text-[var(--text-tertiary,#6B7280)]">
                <GitBranch className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                <p className="text-sm">{t('prozesse.detail.noSteps') || 'Noch keine Schritte definiert'}</p>
              </div>
            ) : (
              renderFlowSvg(schritte)
            )}
          </div>
          {/* Tooltip */}
          {hoveredNode && !dragging && (
            <div style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: 'top center',
              position: 'absolute', top: 16, left: 0, right: 0,
              pointerEvents: 'none',
            }}>
              <FlowTooltip node={hoveredNode} containerRef={containerRef} />
            </div>
          )}
        </div>
      ) : (
        /* Side-by-side compare */
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider mb-2">
              v{(versionen || []).find(x => String(x.version) === String(compareVersion))?.version_label || compareVersion}
            </div>
            <div className="rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-auto p-4" style={{ maxHeight: 600 }}>
              {compareSchritte ? renderFlowSvg(compareSchritte, schritte) : (
                <div className="text-center py-8 text-xs text-[var(--text-tertiary,#6B7280)]">Loading...</div>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider mb-2">
              {t('prozesse.flowchart.current') || 'Aktuell'} (v{versionen?.[0]?.version_label || versionen?.[0]?.version || '?'})
            </div>
            <div className="rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-auto p-4" style={{ maxHeight: 600 }}>
              {renderFlowSvg(schritte, compareSchritte)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function ProzessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user && ['admin', 'administrator', 'ops_manager'].includes(user.rolle);

  // --- Core State ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prozess, setProzess] = useState(null);
  const [schritte, setSchritte] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [medien, setMedien] = useState([]);
  const [verknuepfungen, setVerknuepfungen] = useState([]);

  // --- Tab State ---
  const [activeTab, setActiveTab] = useTabParam('schritte');
  const [moreOpen, setMoreOpen] = useState(false); // #1472 "Mehr"-Overflow-Dropdown
  const [showKpiPanel, setShowKpiPanel] = useState(false); // #1472 KPI-Tab erst nach "KPIs aktivieren" sichtbar

  // --- Lazy-loaded Data ---
  const [kommentare, setKommentare] = useState([]);
  const [kommentareLoaded, setKommentareLoaded] = useState(false);
  const [versionen, setVersionen] = useState([]);
  const [versionenLoaded, setVersionenLoaded] = useState(false);
  const [qrcodes, setQrcodes] = useState([]);
  const [qrcodesLoaded, setQrcodesLoaded] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [changelog, setChangelog] = useState([]);
  const [changelogLoaded, setChangelogLoaded] = useState(false);
  const [showAllChangelog, setShowAllChangelog] = useState(false);

  // --- Notifications (#918) ---
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [notifyEvent, setNotifyEvent] = useState('prozess.aktiv_neu');
  // #1716: per-MA-Empfängerkreis (SSoT) statt Abteilung
  const [notifyMaIds, setNotifyMaIds] = useState([]);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [zuweisungskreis, setZuweisungskreis] = useState([]);
  // #1718-Rework: SMS ist kein wählbarer opt-in-Kanal mehr — sie geht unbedingt an
  // alle zugewiesenen MA mit Stammdaten-Nummer. MA ohne Nummer → HR-Flag „Nummer fehlt".

  // #1731: Zuweisungskreis-Editor (SSoT prozess_zuweisungen) — Add/Remove zentral hier.
  const [kreis, setKreis] = useState([]);
  const [showKreisAdd, setShowKreisAdd] = useState(false);
  const [kreisMas, setKreisMas] = useState([]);
  const [kreisQuery, setKreisQuery] = useState('');
  const [kreisSelIds, setKreisSelIds] = useState([]);
  const [kreisBusy, setKreisBusy] = useState(false);
  const [kreisMsg, setKreisMsg] = useState(null);

  async function loadKreis() {
    try {
      const res = await fetch(`/api/prozesse/${id}/zuweisungen`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setKreis(d.items || []);
      }
    } catch {}
  }

  async function openKreisAdd() {
    setKreisSelIds([]);
    setKreisQuery('');
    setShowKreisAdd(true);
    if (!kreisMas.length) {
      try {
        const res = await fetch('/api/mitarbeiter?aktiv=1', { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setKreisMas(Array.isArray(d) ? d : (d?.rows || d?.data || []));
        }
      } catch {}
    }
  }

  async function kreisAdd() {
    if (!kreisSelIds.length) return;
    setKreisBusy(true);
    setKreisMsg(null);
    try {
      const res = await fetch(`/api/prozesse/${id}/zuweisungen`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_ids: kreisSelIds }),
      });
      const d = await res.json();
      if (!res.ok) {
        setKreisMsg({ error: d.error || 'add_failed' });
      } else {
        setKreisMsg({ added: d.added?.length || 0, test_rows: d.test_rows_created || 0 });
        setShowKreisAdd(false);
        await Promise.all([loadKreis(), loadZuweisungskreis()]);
      }
    } catch (e) {
      setKreisMsg({ error: e.message });
    } finally {
      setKreisBusy(false);
    }
  }

  function kreisRemove(m) {
    setConfirmDialog({
      open: true,
      title: t('prozesse.kreis.removeTitle') || 'Aus Kreis entfernen',
      message: (t('prozesse.kreis.removeMessage') || 'MA aus dem Zuweisungskreis entfernen? Offene Schulungs-Zuweisungen werden gelöscht, bestandene bleiben erhalten.') + ` (${m.name})`,
      onConfirm: async () => {
        setConfirmDialog({ open: false });
        setKreisMsg(null);
        try {
          const res = await fetch(`/api/prozesse/${id}/zuweisungen/${m.id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          const d = await res.json();
          if (!res.ok) setKreisMsg({ error: d.error || 'remove_failed' });
          else setKreisMsg({ removed: m.name, offene_geloescht: d.offene_geloescht || 0 });
        } catch (e) {
          setKreisMsg({ error: e.message });
        }
        await Promise.all([loadKreis(), loadZuweisungskreis()]);
      },
    });
  }

  async function loadNotifications() {
    try {
      const res = await fetch(`/api/prozesse/${id}/notifications`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setNotifications(d.items || []);
        setNotificationsLoaded(true);
      }
    } catch {}
  }

  async function loadZuweisungskreis() {
    try {
      const res = await fetch(`/api/prozesse/${id}/zuweisungskreis`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const items = d.items || [];
        setZuweisungskreis(items);
        // Default: ganzer Kreis vorausgewählt
        setNotifyMaIds(items.map(m => m.id));
      }
    } catch {}
  }

  async function sendNotification() {
    if (!notifyMaIds.length) return;
    setNotifySending(true);
    setNotifyResult(null);
    try {
      const res = await fetch(`/api/prozesse/${id}/notify-zuweisungen`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_key: notifyEvent,
          mitarbeiter_ids: notifyMaIds,
          // #1718-Rework: SMS ist unbedingter Zusatzkanal (Server erzwingt sie ohnehin).
          channels: ['in_app', 'telegram', 'sms'],
        }),
      });
      const d = await res.json();
      if (!res.ok) {
        setNotifyResult({ error: d.error || 'send_failed' });
      } else {
        setNotifyResult(d);
        await loadNotifications();
      }
    } catch (e) {
      setNotifyResult({ error: e.message });
    } finally {
      setNotifySending(false);
    }
  }

  // --- Edit States ---
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [editingStepId, setEditingStepId] = useState(null);
  const [addingStep, setAddingStep] = useState(false);
  const [newStep, setNewStep] = useState({ titel: '', beschreibung: '', verantwortlich: '', system_verknuepfung: '', dauer_soll: '', schritt_typ: 'aktion' });
  const [editingKpiId, setEditingKpiId] = useState(null);
  const [addingKpi, setAddingKpi] = useState(false);
  const [kommentarText, setKommentarText] = useState('');
  const [sending, setSending] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [lightboxState, setLightboxState] = useState({ items: null, index: 0 });
  const openMediaLightbox = useCallback((items, index) => {
    setLightboxState({ items, index });
  }, []);

  // --- Drag State ---
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  // --- Dialogs ---
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [rejectKommentar, setRejectKommentar] = useState('');
  const [versionModal, setVersionModal] = useState({ open: false, data: null });

  // --- Lifecycle (#917) ---
  const [lifecycleLog, setLifecycleLog] = useState([]);
  const [lifecycleLoaded, setLifecycleLoaded] = useState(false);
  const [versionBumpModal, setVersionBumpModal] = useState({ open: false, type: 'patch', begruendung: '' });
  const [reasonModal, setReasonModal] = useState({ open: false, action: null, title: '', begruendung: '' });
  const [genehmigerModal, setGenehmigerModal] = useState({ open: false, userId: '', begruendung: '' });
  // #1474 Änderungs-Workflow
  const [einreichenModal, setEinreichenModal] = useState({ open: false, userId: '', bump_level: 'minor' });
  const [aenderungBusy, setAenderungBusy] = useState(false);
  // #1474 Stufe 2: Nach-Genehmigung-Dialog (Betroffene benachrichtigen + Schulung-Pflicht)
  const [nachModal, setNachModal] = useState({ open: false, parentId: null, versionLabel: '', abteilungName: '', betroffene: [], tests: [], selectedMa: [], selectedTests: [], benachrichtigen: true, schulungPflicht: false, busy: false });
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoaded, setAdminUsersLoaded] = useState(false);
  const [diffModal, setDiffModal] = useState({ open: false, vA: null, vB: null, data: null, loading: false });

  // --- Tags ---
  const [newTag, setNewTag] = useState('');

  // --- Verknuepfung Form ---
  const [addingVerkn, setAddingVerkn] = useState(false);
  const [verknForm, setVerknForm] = useState({ verknuepfung_typ: 'kunde', referenz_id: '', referenz_name: '' });

  // --- File Upload ---
  const fileInputRef = useRef(null);

  // ==========================================================================
  // API HELPERS
  // ==========================================================================
  const apiFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, { credentials: 'include', ...opts });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${res.status}`);
    }
    return res.json();
  }, []);

  const apiMutate = useCallback(async (path, method, body) => {
    const opts = { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
    const res = await fetch(`${API}${path}`, opts);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const e = new Error(body.message || body.error || `HTTP ${res.status}`);
      e.status = res.status;
      Object.assign(e, body); // #1474: Body-Felder (z.B. draft_id) am Error erhalten
      throw e;
    }
    return res.json();
  }, []);

  // ==========================================================================
  // INITIAL LOAD
  // ==========================================================================
  const loadProzess = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiFetch(`/api/prozesse/${id}`);
      if (!data) throw new Error('Keine Daten');
      setProzess(data.prozess);
      setSchritte(data.schritte || []);
      setKpis(data.kpis || []);
      setMedien(data.medien || []);
      setVerknuepfungen(data.verknuepfungen || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, apiFetch]);

  useEffect(() => { loadProzess(); }, [loadProzess]);

  // --- Lazy Loads ---
  const loadKommentare = useCallback(async () => {
    if (kommentareLoaded) return;
    try {
      const data = await apiFetch(`/api/prozesse/${id}/kommentare`);
      setKommentare(data.kommentare || []);
      setKommentareLoaded(true);
    } catch { /* ignore */ }
  }, [id, kommentareLoaded, apiFetch]);

  const loadVersionen = useCallback(async () => {
    if (versionenLoaded) return;
    try {
      const data = await apiFetch(`/api/prozesse/${id}/versionen`);
      setVersionen(data.versionen || []);
      setVersionenLoaded(true);
    } catch { /* ignore */ }
  }, [id, versionenLoaded, apiFetch]);

  const loadChangelog = useCallback(async () => {
    if (changelogLoaded) return;
    try {
      const data = await apiFetch(`/api/prozesse/${id}/changelog`);
      setChangelog(data.changelog || []);
      setChangelogLoaded(true);
    } catch { /* ignore */ }
  }, [id, changelogLoaded, apiFetch]);

  const loadQrcodes = useCallback(async () => {
    if (qrcodesLoaded) return;
    try {
      const data = await apiFetch(`/api/prozesse/${id}/qrcodes`);
      setQrcodes(data || []);
      setQrcodesLoaded(true);
    } catch { /* ignore */ }
  }, [id, qrcodesLoaded, apiFetch]);

  // #1014 — declared early so the useEffect dep-array below does not hit a TDZ
  const loadLifecycleLog = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/prozesse/${id}/lifecycle/log`);
      setLifecycleLog(data.eintraege || []);
      setLifecycleLoaded(true);
    } catch { /* ignore */ }
  }, [id, apiFetch]);

  const loadAdminUsers = useCallback(async () => {
    if (adminUsersLoaded) return;
    try {
      const res = await fetch(`${API}/api/users?rolle=administrator`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        let users = data.users || data || [];
        users = users.filter(u => ['admin', 'administrator', 'ops_manager'].includes(u.rolle));
        setAdminUsers(users);
        setAdminUsersLoaded(true);
      }
    } catch { /* ignore */ }
  }, [adminUsersLoaded]);

  useEffect(() => {
    if (activeTab === 'kommentare') loadKommentare();
    if (activeTab === 'versionen' || activeTab === 'flowchart' || activeTab === 'lifecycle') loadVersionen();
    if (activeTab === 'qrcodes') loadQrcodes();
    if (activeTab === 'lifecycle' && !lifecycleLoaded) loadLifecycleLog();
    if (activeTab === 'notifications' && !notificationsLoaded) {
      loadNotifications();
      loadZuweisungskreis();
      loadKreis(); // #1731 Kreis-Editor
    }
  }, [activeTab, loadKommentare, loadVersionen, loadQrcodes, loadLifecycleLog, lifecycleLoaded, notificationsLoaded]);

  useEffect(() => { loadChangelog(); }, [loadChangelog]);

  // ==========================================================================
  // PROZESS UPDATE HELPERS
  // ==========================================================================
  const updateProzess = async (fields) => {
    try {
      await apiMutate(`/api/prozesse/${id}`, 'PUT', fields);
      setProzess(prev => ({ ...prev, ...fields }));
    } catch { /* ignore */ }
  };

  // --- Title inline edit ---
  const startEditTitle = () => { setTitleDraft(prozess.titel); setEditingTitle(true); };
  const saveTitle = async () => {
    if (titleDraft.trim() && titleDraft !== prozess.titel) await updateProzess({ titel: titleDraft.trim() });
    setEditingTitle(false);
  };

  // --- Description inline edit ---
  const startEditDesc = () => { setDescDraft(prozess.beschreibung || ''); setEditingDesc(true); };
  const saveDesc = async () => {
    if (descDraft !== (prozess.beschreibung || '')) await updateProzess({ beschreibung: descDraft });
    setEditingDesc(false);
  };

  // ==========================================================================
  // SCHRITTE
  // ==========================================================================
  const saveStep = async (form) => {
    try {
      const updatedSchritte = schritte.map(s => s.id === editingStepId ? { ...s, ...form } : s);
      await apiMutate(`/api/prozesse/${id}/schritte`, 'PUT', { schritte: updatedSchritte, aenderungsgrund: 'Schritt bearbeitet' });
      setSchritte(updatedSchritte);
      setEditingStepId(null);
    } catch { /* ignore */ }
  };

  const addStep = async () => {
    if (!newStep.titel.trim()) return;
    try {
      const data = await apiMutate(`/api/prozesse/${id}/schritte`, 'POST', newStep);
      setSchritte(prev => [...prev, data.schritt || { ...newStep, id: Date.now(), sort_order: prev.length + 1 }]);
      setNewStep({ titel: '', beschreibung: '', verantwortlich: '', system_verknuepfung: '', dauer_soll: '', schritt_typ: 'aktion' });
      setAddingStep(false);
    } catch { /* ignore */ }
  };

  const deleteStep = async (schrittId) => {
    try {
      await fetch(`${API}/api/prozesse/${id}/schritte/${schrittId}`, { method: 'DELETE', credentials: 'include' });
      setSchritte(prev => prev.filter(s => s.id !== schrittId));
    } catch { /* ignore */ }
  };

  // --- Drag & Drop ---
  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragOver = (e, idx) => { e.preventDefault(); dragOver.current = idx; };
  const handleDrop = async () => {
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) return;
    const items = [...schritte];
    const dragged = items.splice(dragItem.current, 1)[0];
    items.splice(dragOver.current, 0, dragged);
    const reordered = items.map((s, i) => ({ ...s, sort_order: i + 1 }));
    setSchritte(reordered);
    dragItem.current = null;
    dragOver.current = null;
    try {
      await apiMutate(`/api/prozesse/${id}/schritte`, 'PUT', { schritte: reordered, aenderungsgrund: 'Schritte neu sortiert' });
    } catch { /* ignore */ }
  };

  // ==========================================================================
  // KPIs
  // ==========================================================================
  const saveKpi = async (form) => {
    try {
      if (editingKpiId) {
        await apiMutate(`/api/prozesse/${id}/kpis/${editingKpiId}`, 'PUT', form);
        setKpis(prev => prev.map(k => k.id === editingKpiId ? { ...k, ...form } : k));
        setEditingKpiId(null);
      } else {
        const data = await apiMutate(`/api/prozesse/${id}/kpis`, 'POST', form);
        setKpis(prev => [...prev, data.kpi || { ...form, id: Date.now() }]);
        setAddingKpi(false);
      }
    } catch { /* ignore */ }
  };

  const deleteKpi = async (kpiId) => {
    try {
      await fetch(`${API}/api/prozesse/${id}/kpis/${kpiId}`, { method: 'DELETE', credentials: 'include' });
      setKpis(prev => prev.filter(k => k.id !== kpiId));
    } catch { /* ignore */ }
  };

  // ==========================================================================
  // MEDIEN
  // ==========================================================================
  const uploadFile = async (file) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(`${API}/api/prozesse/${id}/medien`, { method: 'POST', credentials: 'include', body: form });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setMedien(prev => [...prev, data.medium || { id: Date.now(), dateiname: file.name, dateityp: file.type.startsWith('image') ? 'image' : 'document', dateigroesse: file.size, mime_type: file.type }]);
    } catch { /* ignore */ }
  };

  const deleteMedia = async (medienId) => {
    try {
      await fetch(`${API}/api/prozesse/${id}/medien/${medienId}`, { method: 'DELETE', credentials: 'include' });
      setMedien(prev => prev.filter(m => m.id !== medienId));
    } catch { /* ignore */ }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    files.forEach(uploadFile);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = '';
  };

  const mediaIcon = (typ) => {
    if (typ === 'image') return <Image className="w-8 h-8 text-[#338edc]" strokeWidth={1.5} />;
    if (typ === 'video') return <Video className="w-8 h-8 text-[#6366F1]" strokeWidth={1.5} />;
    if (typ === 'audio') return <Mic className="w-8 h-8 text-[#22D3EE]" strokeWidth={1.5} />;
    return <FileText className="w-8 h-8 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />;
  };

  const streamUrl = (path) => `${API}/api/prozesse/medien/stream/${path}`;
  const downloadUrl = (path) => `${API}/api/prozesse/medien/download/${path}`;

  // ==========================================================================
  // KOMMENTARE
  // ==========================================================================
  const sendKommentar = async () => {
    if (!kommentarText.trim() || sending) return;
    setSending(true);
    try {
      const data = await apiMutate(`/api/prozesse/${id}/kommentare`, 'POST', { text: kommentarText.trim() });
      setKommentare(prev => [...prev, data.kommentar || { id: Date.now(), text: kommentarText.trim(), benutzer_name: 'Du', erstellt_am: new Date().toISOString() }]);
      setKommentarText('');
    } catch { /* ignore */ }
    setSending(false);
  };

  // ==========================================================================
  // VERSIONEN
  // ==========================================================================
  const viewVersion = async (version) => {
    try {
      const data = await apiFetch(`/api/prozesse/${id}/versionen/${version}`);
      setVersionModal({ open: true, data: data.version });
    } catch { /* ignore */ }
  };

  // ==========================================================================
  // WORKFLOW ACTIONS
  // ==========================================================================
  const requestReview = async () => {
    try {
      await apiMutate(`/api/prozesse/${id}/review`, 'POST', {});
      setProzess(prev => ({ ...prev, status: 'in_review' }));
    } catch { /* ignore */ }
  };

  const approveProzess = async () => {
    try {
      await apiMutate(`/api/prozesse/${id}/freigabe`, 'POST', { kommentar: '' });
      setProzess(prev => ({ ...prev, status: 'aktiv' }));
    } catch { /* ignore */ }
  };

  const rejectProzess = async () => {
    try {
      await apiMutate(`/api/prozesse/${id}/ablehnen`, 'POST', { kommentar: rejectKommentar });
      setProzess(prev => ({ ...prev, status: 'entwurf' }));
      setConfirmDialog({ open: false });
      setRejectKommentar('');
    } catch { /* ignore */ }
  };

  const archiveProzess = async (begruendung) => {
    try {
      await apiMutate(`/api/prozesse/${id}/archivieren`, 'POST', { begruendung });
      setProzess(prev => ({ ...prev, status: 'archiviert' }));
      setConfirmDialog({ open: false });
      setReasonModal({ open: false, action: null, title: '', begruendung: '' });
      setLifecycleLoaded(false);
    } catch (err) { alert(err.message || 'Archivieren fehlgeschlagen'); }
  };

  // ==========================================================================
  // LIFECYCLE (#917) — loadLifecycleLog + loadAdminUsers wurden nach oben
  // verschoben (#1014, TDZ-Fix für useEffect-dep-array)
  // ==========================================================================
  const entsperrenProzess = async (begruendung) => {
    if (!begruendung || !begruendung.trim()) { alert(t('prozesse.lifecycle.reasonRequired') || 'Begründung erforderlich'); return; }
    try {
      await apiMutate(`/api/prozesse/${id}/lifecycle/entsperren`, 'POST', { begruendung });
      setProzess(prev => ({ ...prev, status: 'entsperrt' }));
      setReasonModal({ open: false, action: null, title: '', begruendung: '' });
      setLifecycleLoaded(false);
    } catch (err) { alert(err.message || 'Entsperren fehlgeschlagen'); }
  };

  const sperrenWiederProzess = async (begruendung) => {
    try {
      await apiMutate(`/api/prozesse/${id}/lifecycle/sperren-wieder`, 'POST', { begruendung });
      setProzess(prev => ({ ...prev, status: 'aktiv' }));
      setReasonModal({ open: false, action: null, title: '', begruendung: '' });
      setLifecycleLoaded(false);
    } catch (err) { alert(err.message || 'Sperren fehlgeschlagen'); }
  };

  const versionBump = async (type, begruendung) => {
    if (!begruendung || !begruendung.trim()) { alert(t('prozesse.lifecycle.reasonRequired') || 'Begründung erforderlich'); return; }
    try {
      const data = await apiMutate(`/api/prozesse/${id}/lifecycle/version-bump`, 'POST', { type, begruendung });
      if (data.version) {
        setProzess(prev => ({
          ...prev,
          aktuelle_version: data.version.major,
          version_minor: data.version.minor,
          version_patch: data.version.patch,
        }));
      }
      setVersionBumpModal({ open: false, type: 'patch', begruendung: '' });
      setLifecycleLoaded(false);
      setVersionenLoaded(false);
    } catch (err) { alert(err.message || 'Version-Bump fehlgeschlagen'); }
  };

  const setGenehmiger = async (userId, begruendung) => {
    try {
      await apiMutate(`/api/prozesse/${id}/genehmiger`, 'PUT', {
        genehmiger_id: userId || null,
        begruendung: begruendung || null,
      });
      // Reload prozess to get updated genehmiger_name (via JOIN)
      await loadProzess();
      setGenehmigerModal({ open: false, userId: '', begruendung: '' });
      setLifecycleLoaded(false);
    } catch (err) { alert(err.message || 'Genehmiger setzen fehlgeschlagen'); }
  };

  // ==========================================================================
  // #1474 ÄNDERUNGS-WORKFLOW (Change-Management)
  // ==========================================================================
  // Gesperrter Prozess → Änderungsentwurf (Kopie) anlegen, dann zum Entwurf navigieren.
  const aenderungVorschlagen = async () => {
    if (aenderungBusy) return;
    setAenderungBusy(true);
    try {
      const data = await apiMutate(`/api/prozesse/${id}/aenderung-vorschlagen`, 'POST', {});
      if (data?.draft_id) navigate(`/prozesse/${data.draft_id}`);
    } catch (err) {
      // AK6: existiert bereits ein offener Entwurf → dorthin navigieren
      if (err?.draft_id) { navigate(`/prozesse/${err.draft_id}`); return; }
      alert(err.message || 'Änderung vorschlagen fehlgeschlagen');
    } finally { setAenderungBusy(false); }
  };

  // Entwurf zur Genehmigung einreichen (Genehmiger + Bump-Stufe).
  const aenderungEinreichen = async () => {
    if (!einreichenModal.userId) { alert(t('prozesse.aenderung.genehmigerPflicht') || 'Genehmiger ist Pflicht'); return; }
    setAenderungBusy(true);
    try {
      await apiMutate(`/api/prozesse/${id}/aenderung-einreichen`, 'POST', {
        genehmiger_id: einreichenModal.userId,
        bump_level: einreichenModal.bump_level,
      });
      setEinreichenModal({ open: false, userId: '', bump_level: 'minor' });
      await loadProzess();
      setLifecycleLoaded(false);
    } catch (err) { alert(err.message || 'Einreichen fehlgeschlagen'); }
    finally { setAenderungBusy(false); }
  };

  // Genehmiger genehmigt den Entwurf → merge in Mutter + Bump + Archiv. Danach zur Mutter.
  const aenderungGenehmigen = async () => {
    setAenderungBusy(true);
    try {
      const data = await apiMutate(`/api/prozesse/${id}/aenderung-genehmigen`, 'POST', { kommentar: '' });
      const parentId = data?.parent_id || prozess?.parent_prozess_id;
      const versionLabel = data?.version?.string || '';
      // Stufe 2: aktive Abfrage statt stillem Navigieren — Betroffene laden, Dialog öffnen.
      let betr = { betroffene: [], tests: [], abteilung_name: '' };
      try { betr = await apiFetch(`/api/prozesse/${parentId}/betroffene`); } catch { /* fail-soft */ }
      setNachModal({
        open: true, parentId, versionLabel,
        abteilungName: betr.abteilung_name || '',
        betroffene: betr.betroffene || [],
        tests: betr.tests || [],
        selectedMa: (betr.betroffene || []).map(b => b.mitarbeiter_id),
        selectedTests: (betr.tests || []).map(t => t.id),
        benachrichtigen: true, schulungPflicht: false, busy: false,
      });
    } catch (err) { alert(err.message || 'Genehmigen fehlgeschlagen'); }
    finally { setAenderungBusy(false); }
  };

  // Stufe 2: Dialog absenden (Benachrichtigen + Schulung-Pflicht) oder überspringen → zur Mutter.
  const nachGenehmigungSubmit = async (skip) => {
    const m = nachModal;
    const goParent = () => { setNachModal(prev => ({ ...prev, open: false })); if (m.parentId) navigate(`/prozesse/${m.parentId}`); };
    if (skip) { goParent(); return; }
    setNachModal(prev => ({ ...prev, busy: true }));
    try {
      await apiMutate(`/api/prozesse/${m.parentId}/nach-genehmigung`, 'POST', {
        version_label: m.versionLabel,
        benachrichtigen: m.benachrichtigen,
        schulung_pflicht: m.schulungPflicht,
        mitarbeiter_ids: m.selectedMa,
        test_ids: m.selectedTests,
      });
      goParent();
    } catch (err) {
      alert(err.message || 'Aktion fehlgeschlagen');
      setNachModal(prev => ({ ...prev, busy: false }));
    }
  };

  // Genehmiger lehnt ab → Entwurf zurück In Bearbeitung + Kommentar.
  const aenderungAblehnen = async () => {
    if (!rejectKommentar.trim()) { alert(t('prozesse.aenderung.ablehnenKommentarPflicht') || 'Kommentar erforderlich'); return; }
    setAenderungBusy(true);
    try {
      await apiMutate(`/api/prozesse/${id}/aenderung-ablehnen`, 'POST', { kommentar: rejectKommentar });
      setProzess(prev => ({ ...prev, status: 'entwurf' }));
      setConfirmDialog({ open: false });
      setRejectKommentar('');
      setLifecycleLoaded(false);
    } catch (err) { alert(err.message || 'Ablehnen fehlgeschlagen'); }
    finally { setAenderungBusy(false); }
  };

  const openDiff = async (vA, vB) => {
    setDiffModal({ open: true, vA, vB, data: null, loading: true });
    try {
      const data = await apiFetch(`/api/prozesse/${id}/lifecycle/diff/${vA}/${vB}`);
      setDiffModal({ open: true, vA, vB, data, loading: false });
    } catch (err) {
      setDiffModal({ open: true, vA, vB, data: null, loading: false });
      alert(err.message || 'Diff laden fehlgeschlagen');
    }
  };

  // ==========================================================================
  // TAGS
  // ==========================================================================
  const addTag = async () => {
    if (!newTag.trim()) return;
    const tags = [...(prozess.tags || []), newTag.trim()];
    await updateProzess({ tags });
    setNewTag('');
  };

  const removeTag = async (tag) => {
    const tags = (prozess.tags || []).filter(t => t !== tag);
    await updateProzess({ tags });
  };

  // ==========================================================================
  // VERKNUEPFUNGEN
  // ==========================================================================
  const addVerknuepfung = async () => {
    if (!verknForm.referenz_name.trim()) return;
    try {
      const data = await apiMutate(`/api/prozesse/${id}/verknuepfungen`, 'POST', verknForm);
      setVerknuepfungen(prev => [...prev, data.verknuepfung || { ...verknForm, id: Date.now() }]);
      setAddingVerkn(false);
      setVerknForm({ verknuepfung_typ: 'kunde', referenz_id: '', referenz_name: '' });
    } catch { /* ignore */ }
  };

  const deleteVerknuepfung = async (vId) => {
    try {
      await fetch(`${API}/api/prozesse/${id}/verknuepfungen/${vId}`, { method: 'DELETE', credentials: 'include' });
      setVerknuepfungen(prev => prev.filter(v => v.id !== vId));
    } catch { /* ignore */ }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#338edc]" />
      </div>
    );
  }

  if (error || !prozess) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertTriangle className="w-12 h-12 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
        <p className="text-[var(--text-secondary,#4B5563)]">{error || t('prozesse.detail.notFound') || 'Prozess nicht gefunden'}</p>
        <BackLink to="/prozesse" label={t('prozesse.detail.backToList') || t('common.backToList')} />
      </div>
    );
  }

  // #1472 KPIs sind "aktiviert", sobald mindestens ein KPI existiert (Backend verlangt das auch zur Prozess-Aktivierung).
  const kpisActive = kpis.length > 0;

  // #1472 Kern-Tabs immer prominent sichtbar; Rest im "Mehr"-Overflow-Dropdown (alle Funktionen erreichbar).
  const kernTabs = [
    { key: 'schritte', label: t('prozesse.detail.tabSchritte') || 'Schritte', icon: Activity },
    { key: 'flowchart', label: t('prozesse.flowchart.title') || 'Flowchart', icon: GitBranch },
    { key: 'medien', label: t('prozesse.detail.tabMedien') || 'Medien', icon: Image },
    { key: 'kommentare', label: t('prozesse.detail.tabKommentare') || 'Kommentare', icon: MessageSquare },
  ];
  const moreTabs = [
    // KPIs nur, wenn aktiviert (oder explizit per "KPIs aktivieren" eingeblendet) — AK9.
    ...((kpisActive || showKpiPanel || activeTab === 'kpis')
      ? [{ key: 'kpis', label: t('prozesse.detail.tabKpis') || 'KPIs', icon: Target }]
      : []),
    { key: 'qrcodes', label: t('prozesse.qr.title') || 'QR-Codes', icon: QrCode },
    { key: 'versionen', label: t('prozesse.detail.tabVersionen') || 'Versionen', icon: History },
    { key: 'lifecycle', label: t('prozesse.lifecycle.tabAuditTrail') || 'Audit-Trail', icon: Shield },
    // #1731: Tab = Kreis + Benachrichtigung (Kreis steuert Benachrichtigung UND Schulung)
    { key: 'notifications', label: t('prozesse.notify.tabKreis') || 'Zuweisungen & Benachrichtigung', icon: Send },
    { key: 'mctest', label: t('qmTest.tab') || 'MC-Test', icon: Award },
  ];
  const activeMoreTab = moreTabs.find(tb => tb.key === activeTab);

  const editingDisabled = !EDITABLE_STATES.has(prozess.status);
  // #1714: Sperr-Hinweis status-abhängig — im Review ist die Bearbeitung by design gesperrt,
  // die Freigabe läuft aber über Freigeben/Ablehnen (kein „Entsperren" nötig). Nur bei
  // 'aktiv' (Gesperrt) ist Entsperren wirklich der Weg zum Bearbeiten.
  const lockHint = editingDisabled
    ? (prozess.status === 'in_review'
        ? (t('prozesse.lifecycle.editDisabledHintReview') || 'In Review — Bearbeitung ist während der Freigabe gesperrt. Zur Aktivierung unten „Freigeben" oder „Ablehnen" wählen (kein Entsperren nötig).')
        : (t('prozesse.lifecycle.editDisabledHint') || 'Bearbeitung gesperrt — zum Bearbeiten den Prozess entsperren'))
    : '';
  const isDraft = !!prozess.parent_prozess_id; // #1474: Prozess ist ein Änderungsentwurf
  const parentVersionStr = isDraft ? `${prozess.parent_major || 1}.${prozess.parent_minor || 0}.${prozess.parent_patch || 0}` : '';

  return (
    <div className="p-6 md:p-8 flex flex-col lg:flex-row gap-6">
      {/* ================================================================ */}
      {/* LEFT SIDE — 70% */}
      {/* ================================================================ */}
      <div className="flex-1 min-w-0 w-full">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-[var(--text-tertiary,#6B7280)] mb-4 flex-wrap">
          <Link to="/prozesse" className="hover:text-[#338edc] transition-colors">{t('prozesse.title') || 'Prozesse'}</Link>
          <ChevronRight className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
          {prozess.abteilung_name && (
            <>
              <Link to={`/prozesse/abteilungen/${prozess.abteilung_slug}`} className="hover:text-[#338edc] transition-colors">{prozess.abteilung_name}</Link>
              <ChevronRight className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
            </>
          )}
          {prozess.bereich_name && (
            <>
              <span>{prozess.bereich_name}</span>
              <ChevronRight className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
            </>
          )}
          <span className="text-[var(--text-primary,#1A1A2E)] font-medium truncate">{prozess.titel}</span>
        </nav>

        {/* #1474: Banner — dieser Prozess ist ein Änderungsentwurf */}
        {isDraft && (
          <div className="mb-4 rounded-xl border border-[#6366F1]/30 bg-[#6366F1]/5 px-4 py-3 flex items-start gap-3">
            <GitBranch className="w-4 h-4 mt-0.5 shrink-0 text-[#6366F1]" strokeWidth={1.5} />
            <div className="text-sm">
              <p className="font-medium text-[var(--text-primary,#1A1A2E)]">
                {t('prozesse.aenderung.draftBanner') || 'Änderungsentwurf'}
                {' — '}
                {prozess.status === 'in_review'
                  ? (t('prozesse.aenderung.draftInReview') || 'wartet auf Genehmigung')
                  : (t('prozesse.aenderung.draftInBearbeitung') || 'in Bearbeitung')}
              </p>
              <p className="text-[var(--text-tertiary,#6B7280)] mt-0.5">
                {t('prozesse.aenderung.draftBasis') || 'Basis'}: {' '}
                <Link to={`/prozesse/${prozess.parent_prozess_id}`} className="text-[#338edc] hover:underline">
                  {prozess.parent_titel || `#${prozess.parent_prozess_id}`} (v{parentVersionStr})
                </Link>
                {' — '}
                {t('prozesse.aenderung.draftLiveHint') || 'die freigegebene Fassung bleibt unverändert, bis dieser Entwurf genehmigt wird.'}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start gap-3 mb-2">
            {editingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)] bg-transparent border-b-2 border-[#338edc] outline-none flex-1 min-w-0 py-0.5"
              />
            ) : (
              <h1
                className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)] cursor-text hover:bg-[var(--bg-hover,#FAFAFE)] rounded-lg px-1 -mx-1 transition-colors flex-1 min-w-0"
                onClick={startEditTitle}
              >
                {prozess.titel}
              </h1>
            )}
          </div>
          <div className="flex items-center gap-2 mb-3">
            <Badge label={TYP_LABELS[prozess.typ] || prozess.typ} color={TYP_COLORS[prozess.typ] || '#338edc'} />
            <StatusBadge status={prozess.status} t={t} />
          </div>
          {editingDesc ? (
            <div>
              <ProcessRichTextEditor
                value={descDraft}
                onChange={setDescDraft}
                prozessId={id}
                placeholder={t('prozesse.detail.descPlaceholder') || 'Prozess beschreiben — Ueberschriften, Listen, Bilder und Prozess-Links moeglich...'}
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={saveDesc}
                  className="px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2a78bd] transition-colors"
                >
                  {t('common.save') || 'Speichern'}
                </button>
                <button
                  type="button"
                  onClick={() => { setDescDraft(prozess.beschreibung || ''); setEditingDesc(false); }}
                  className="px-3 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary,#4B5563)] text-sm hover:bg-[var(--bg-hover,#FAFAFE)] transition-colors"
                >
                  {t('common.cancel') || 'Abbrechen'}
                </button>
              </div>
            </div>
          ) : (
            <ProcessHtmlView
              html={prozess.beschreibung}
              onEmptyClick={startEditDesc}
              emptyPlaceholder={t('prozesse.detail.addDescription') || 'Beschreibung hinzufuegen...'}
            />
          )}
          {!editingDesc && prozess.beschreibung && (
            <button
              type="button"
              onClick={startEditDesc}
              className="mt-1 text-xs text-[#338edc] hover:underline"
            >
              {t('common.edit') || 'Bearbeiten'}
            </button>
          )}
        </div>

        {/* Tab-Bar — #1472 Kern prominent + "Mehr"-Overflow-Dropdown */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)] inline-flex flex-wrap">
            {kernTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-[8px] transition-all ${isActive
                    ? 'bg-[var(--bg-card,#fff)] shadow-sm font-semibold text-[var(--text-primary,#1A1A2E)]'
                    : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="relative">
            <button
              onClick={() => setMoreOpen(o => !o)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-[10px] border transition-all ${activeMoreTab
                ? 'bg-[var(--bg-card,#fff)] border-[#338edc] font-semibold text-[#338edc]'
                : 'border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'
              }`}
            >
              <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
              {activeMoreTab ? activeMoreTab.label : (t('prozesse.detail.tabMore') || 'Mehr')}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)} />
                <div className="absolute left-0 top-full mt-1 z-20 min-w-[210px] rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] shadow-lg p-1.5">
                  {moreTabs.map(tab => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => { setActiveTab(tab.key); setMoreOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${isActive
                          ? 'bg-[#338edc]/10 font-semibold text-[#338edc]'
                          : 'text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]'
                        }`}
                      >
                        <Icon className="w-4 h-4" strokeWidth={1.5} />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[300px]">
          {/* === SCHRITTE TAB === */}
          {activeTab === 'schritte' && (
            <div>
              {schritte.length === 0 && !addingStep && (
                <div className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">
                  <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="text-sm">{t('prozesse.detail.noSteps') || 'Noch keine Schritte definiert'}</p>
                </div>
              )}
              {schritte.map((step, idx) => (
                <StepCard
                  key={step.id}
                  step={step}
                  index={idx}
                  editing={editingStepId === step.id}
                  onEdit={() => setEditingStepId(step.id)}
                  onSave={saveStep}
                  onCancel={() => setEditingStepId(null)}
                  onDelete={() => setConfirmDialog({
                    open: true, title: t('prozesse.detail.deleteStep') || 'Schritt loeschen',
                    message: t('prozesse.detail.deleteStepConfirm') || 'Diesen Schritt wirklich loeschen?',
                    onConfirm: () => { deleteStep(step.id); setConfirmDialog({ open: false }); }
                  })}
                  dragHandlers={{
                    onDragStart: () => handleDragStart(idx),
                    onDragOver: (e) => handleDragOver(e, idx),
                    onDrop: handleDrop,
                    onDragEnd: () => { dragItem.current = null; dragOver.current = null; },
                  }}
                  prozessId={id}
                  onMediaChanged={loadProzess}
                  onOpenLightbox={openMediaLightbox}
                  t={t}
                />
              ))}
              {addingStep ? (
                <div className="rounded-xl border border-[#338edc] bg-[var(--bg-card,#fff)] p-4 mb-3 space-y-3">
                  <input value={newStep.titel} onChange={e => setNewStep({ ...newStep, titel: e.target.value })} placeholder="Schritt-Titel"
                    className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc]" autoFocus />
                  <ProcessRichTextEditor
                    value={newStep.beschreibung}
                    onChange={(html) => setNewStep({ ...newStep, beschreibung: html })}
                    prozessId={id}
                    placeholder={t('prozesse.detail.descPlaceholder') || 'Beschreibung'}
                    minHeight={140}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input value={newStep.verantwortlich} onChange={e => setNewStep({ ...newStep, verantwortlich: e.target.value })} placeholder="Verantwortlich"
                      className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
                    <input value={newStep.system_verknuepfung} onChange={e => setNewStep({ ...newStep, system_verknuepfung: e.target.value })} placeholder="System"
                      className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
                    <input type="number" step="0.1" value={newStep.dauer_soll} onChange={e => setNewStep({ ...newStep, dauer_soll: e.target.value })} placeholder="Dauer (min)"
                      className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]" />
                    <select value={newStep.schritt_typ} onChange={e => setNewStep({ ...newStep, schritt_typ: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]">
                      <option value="aktion">Aktion</option>
                      <option value="entscheidung">Entscheidung</option>
                      <option value="warten">Warten</option>
                      <option value="pruefung">Pruefung</option>
                    </select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setAddingStep(false)} className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)]">Abbrechen</button>
                    <button onClick={addStep} disabled={!newStep.titel.trim()} className="px-3 py-1.5 text-sm rounded-lg text-white font-medium bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-40">{t('prozesse.detail.addStep') || 'Hinzufuegen'}</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAddingStep(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl border border-dashed border-[var(--border-card,#E5E7EB)] text-[var(--text-tertiary,#6B7280)] hover:border-[#338edc] hover:text-[#338edc] transition-colors w-full justify-center">
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.addStepBtn') || 'Schritt hinzufuegen'}
                </button>
              )}
            </div>
          )}

          {/* === KPI TAB === */}
          {activeTab === 'kpis' && (
            <div>
              {kpis.length === 0 && !addingKpi && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-amber-700">{t('prozesse.detail.kpiRequired') || 'Mindestens ein KPI erforderlich fuer die Aktivierung'}</p>
                </div>
              )}
              {kpis.map(kpi => {
                const messung = null; // #1723: Live-Messung (Health) entfernt — KPI-Definition bleibt
                return editingKpiId === kpi.id ? (
                  <KpiForm key={kpi.id} initial={kpi} onSave={saveKpi} onCancel={() => setEditingKpiId(null)} />
                ) : (
                  <KpiCard
                    key={kpi.id}
                    kpi={kpi}
                    messung={messung}
                    onEdit={() => setEditingKpiId(kpi.id)}
                    onDelete={() => setConfirmDialog({
                      open: true, title: t('prozesse.detail.deleteKpi') || 'KPI loeschen',
                      message: t('prozesse.detail.deleteKpiConfirm') || 'Dieses KPI wirklich loeschen?',
                      onConfirm: () => { deleteKpi(kpi.id); setConfirmDialog({ open: false }); }
                    })}
                  />
                );
              })}
              {addingKpi ? (
                <KpiForm onSave={saveKpi} onCancel={() => setAddingKpi(false)} />
              ) : (
                <button onClick={() => setAddingKpi(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl border border-dashed border-[var(--border-card,#E5E7EB)] text-[var(--text-tertiary,#6B7280)] hover:border-[#338edc] hover:text-[#338edc] transition-colors w-full justify-center">
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.addKpi') || 'KPI hinzufuegen'}
                </button>
              )}
            </div>
          )}

          {/* === FLOWCHART TAB === */}
          {activeTab === 'flowchart' && (
            <ProzessFlowchart
              schritte={schritte}
              versionen={versionenLoaded ? versionen : []}
              t={t}
              onClickStep={(idx) => {
                setActiveTab('schritte');
                // Scroll to step after tab switch
                setTimeout(() => {
                  const stepCards = document.querySelectorAll('[data-step-index]');
                  if (stepCards[idx]) stepCards[idx].scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
              }}
            />
          )}

          {/* === MEDIEN TAB === */}
          {activeTab === 'medien' && (
            <div>
              {/* Upload Zone */}
              <div
                onDrop={handleFileDrop}
                onDragOver={e => e.preventDefault()}
                className="rounded-xl border-2 border-dashed border-[var(--border-card,#E5E7EB)] hover:border-[#338edc] transition-colors p-8 text-center mb-6 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
                <p className="text-sm text-[var(--text-secondary,#4B5563)]">{t('prozesse.detail.dropFiles') || 'Dateien hierher ziehen oder'}</p>
                <button className="mt-2 px-4 py-1.5 text-sm rounded-lg bg-[#338edc] text-white hover:bg-[#2b7bc4] font-medium">
                  {t('prozesse.detail.selectFiles') || 'Dateien auswaehlen'}
                </button>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
              </div>

              {medien.length === 0 ? (
                <div className="text-center py-8 text-[var(--text-tertiary,#6B7280)]">
                  <Image className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="text-sm">{t('prozesse.detail.noMedia') || 'Noch keine Medien hochgeladen'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {medien.map(m => (
                    <div key={m.id} className="group rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
                      {m.dateityp === 'image' ? (
                        <div className="aspect-video bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center overflow-hidden cursor-pointer relative"
                          onClick={() => setLightboxImg(streamUrl(m.dateipfad))}>
                          <img
                            src={streamUrl(m.dateipfad)}
                            alt={m.dateiname}
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.fallback-icon')?.classList.remove('hidden'); }}
                          />
                          <div className="fallback-icon hidden items-center justify-center w-full h-full">
                            <Image className="w-10 h-10 text-[var(--text-tertiary,#6B7280)] opacity-30" strokeWidth={1.5} />
                          </div>
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-80 transition-opacity drop-shadow" strokeWidth={2} />
                          </div>
                        </div>
                      ) : m.dateityp === 'video' ? (
                        <div className="aspect-video bg-black rounded-t-xl overflow-hidden">
                          <video
                            src={streamUrl(m.dateipfad)}
                            controls
                            preload="metadata"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : m.dateityp === 'audio' ? (
                        <div className="aspect-video bg-[var(--bg-inset,#F8F9FC)] flex flex-col items-center justify-center gap-3 px-4">
                          <div className="w-12 h-12 rounded-full bg-[#22D3EE]/10 flex items-center justify-center">
                            <Mic className="w-6 h-6 text-[#22D3EE]" strokeWidth={1.5} />
                          </div>
                          <audio src={streamUrl(m.dateipfad)} controls preload="metadata" className="w-full max-w-[240px]" />
                        </div>
                      ) : (
                        <div className="aspect-video bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center">
                          {mediaIcon(m.dateityp)}
                        </div>
                      )}
                      <div className="p-3">
                        <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] truncate">{m.dateiname}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{formatFileSize(m.dateigroesse)}</span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a href={downloadUrl(m.dateipfad)} download className="p-1 rounded hover:bg-[var(--bg-hover,#FAFAFE)]">
                              <Download className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
                            </a>
                            <button onClick={() => deleteMedia(m.id)} className="p-1 rounded hover:bg-red-50">
                              <Trash2 className="w-3.5 h-3.5 text-red-400" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                        {m.quelle === 'telegram' && (
                          <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-[#E3F2FD] text-[#1976D2]">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
                            Telegram
                          </span>
                        )}
                        {m.beschreibung && <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1 truncate">{m.beschreibung}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === LIGHTBOX (shared) === */}
          {lightboxImg && (
            <MediaLightbox
              items={[{ url: lightboxImg, dateiname: '', mime_type: 'image/*' }]}
              currentIndex={0}
              onClose={() => setLightboxImg(null)}
              onNavigate={() => {}}
              t={t}
            />
          )}
          {lightboxState.items && (
            <MediaLightbox
              items={lightboxState.items}
              currentIndex={lightboxState.index}
              onClose={() => setLightboxState({ items: null, index: 0 })}
              onNavigate={(i) => setLightboxState(s => ({ ...s, index: i }))}
              t={t}
            />
          )}

          {/* === QR-CODES TAB === */}
          {activeTab === 'qrcodes' && (
            <div>
              {qrcodes.length === 0 && !showQrModal && (
                <div className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">
                  <QrCode className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="text-sm">{t('prozesse.qr.noQrCodes') || 'Noch keine QR-Codes erstellt'}</p>
                </div>
              )}

              {qrcodes.length > 0 && (
                <div className="space-y-3 mb-4">
                  {qrcodes.map(qr => (
                    <div key={qr.id} className="flex items-start gap-3 p-3 rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)]">
                      <div className="w-10 h-10 rounded-lg bg-[#EBF4FD] flex items-center justify-center shrink-0">
                        <QrCode className="w-5 h-5 text-[#338edc]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{qr.standort_name}</span>
                          {qr.standort_typ && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]">
                              {t(`prozesse.qr.standortTyp.${qr.standort_typ}`) || qr.standort_typ}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5">
                          {qr.schritt_titel ? `→ ${t('prozesse.qr.step') || 'Schritt'} ${qr.schritt_nr}: ${qr.schritt_titel}` : `→ ${t('prozesse.qr.entireProcess') || 'Gesamter Prozess'}`}
                        </p>
                        <p className="text-[10px] text-[var(--text-muted,#9CA3AF)] mt-1">
                          {qr.scan_count} {t('prozesse.qr.scans') || 'Scans'}
                          {qr.letzter_scan && ` · ${t('prozesse.qr.lastScanShort') || 'Letzter'}: ${new Date(qr.letzter_scan).toLocaleDateString('de-AT')}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={`${API}/api/prozesse/qrcodes/${qr.id}/png`} download
                          className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]" title={t('prozesse.qr.downloadPng') || 'PNG'}>
                          <Download className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
                        </a>
                        <a href={`${API}/api/prozesse/qrcodes/${qr.id}/pdf`} download
                          className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]" title={t('prozesse.qr.downloadPdf') || 'PDF'}>
                          <FileText className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
                        </a>
                        <button onClick={async () => {
                          if (!confirm(t('prozesse.qr.confirmDelete') || 'QR-Code löschen?')) return;
                          await fetch(`${API}/api/prozesse/qrcodes/${qr.id}`, { method: 'DELETE', credentials: 'include' });
                          setQrcodes(prev => prev.filter(q => q.id !== qr.id));
                        }} className="p-1.5 rounded-lg hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5 text-red-400" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={() => setShowQrModal(true)}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4]">
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                {t('prozesse.qr.create') || 'QR-Code erstellen'}
              </button>

              {/* QR-Code Create Modal */}
              {showQrModal && (() => {
                const QrCreateModal = () => {
                  const [sName, setSName] = useState('');
                  const [sTyp, setSTyp] = useState('');
                  const [sSchritt, setSSchritt] = useState('');
                  const [creating, setCreating] = useState(false);

                  const typOptions = [
                    { value: '', label: '–' },
                    { value: 'packtisch', label: t('prozesse.qr.standortTyp.packtisch') || 'Packtisch' },
                    { value: 'regal', label: t('prozesse.qr.standortTyp.regal') || 'Regal' },
                    { value: 'scanner', label: t('prozesse.qr.standortTyp.scanner') || 'Scanner-Station' },
                    { value: 'versandzone', label: t('prozesse.qr.standortTyp.versandzone') || 'Versandzone' },
                    { value: 'sonstige', label: t('prozesse.qr.standortTyp.sonstige') || 'Sonstige' },
                  ];

                  const create = async () => {
                    if (!sName.trim()) return;
                    setCreating(true);
                    try {
                      const res = await fetch(`${API}/api/prozesse/${id}/qrcodes`, {
                        method: 'POST', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          standort_name: sName.trim(),
                          standort_typ: sTyp || null,
                          schritt_id: sSchritt ? parseInt(sSchritt) : null,
                        }),
                      });
                      if (res.ok) {
                        const newQr = await res.json();
                        setQrcodes(prev => [newQr, ...prev]);
                        setShowQrModal(false);
                      }
                    } catch { /* ignore */ }
                    setCreating(false);
                  };

                  return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowQrModal(false)}>
                      <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-sm border border-[var(--border-card,#E5E7EB)] w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-4">
                          <QrCode className="w-5 h-5 text-[#338edc]" />
                          <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.qr.create') || 'QR-Code erstellen'}</h3>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('prozesse.qr.standortName') || 'Standort-Name'} *</label>
                            <input value={sName} onChange={e => setSName(e.target.value)} placeholder="z.B. Packtisch 3, Regal A-14"
                              className="w-full px-3 py-2 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-primary,#fff)] focus:border-[#338edc] focus:outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('prozesse.qr.standortTyp') || 'Standort-Typ'}</label>
                            <select value={sTyp} onChange={e => setSTyp(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-primary,#fff)]">
                              {typOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('prozesse.qr.linkedStep') || 'Verlinkt zu Schritt'}</label>
                            <select value={sSchritt} onChange={e => setSSchritt(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-primary,#fff)]">
                              <option value="">{t('prozesse.qr.entireProcess') || 'Gesamter Prozess'}</option>
                              {schritte.map((s, i) => <option key={s.id} value={s.id}>{i + 1}. {s.titel}</option>)}
                            </select>
                          </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                          <button onClick={() => setShowQrModal(false)}
                            className="px-4 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)]">
                            {t('common.cancel') || 'Abbrechen'}
                          </button>
                          <button onClick={create} disabled={!sName.trim() || creating}
                            className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-50">
                            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                            {t('prozesse.qr.createAndDownload') || 'Erstellen'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                };
                return <QrCreateModal />;
              })()}
            </div>
          )}

          {/* #1723 P1 Schritt 2: AUSFÜHRUNGEN-Tab entfernt (System B) */}

          {/* === KOMMENTARE TAB === */}
          {activeTab === 'kommentare' && (
            <div>
              {kommentare.length === 0 && (
                <div className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="text-sm">{t('prozesse.detail.noComments') || 'Noch keine Kommentare'}</p>
                </div>
              )}
              <div className="space-y-4 mb-6">
                {kommentare.map(k => (
                  <div key={k.id} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#338edc]/10 flex items-center justify-center text-xs font-semibold text-[#338edc] shrink-0">
                      {initials(k.benutzer_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{k.benutzer_name}</span>
                        <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{formatDate(k.erstellt_am)}</span>
                        {k.typ === 'abweichung' && <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-[#FEF3C7] text-[#D97706]">Abweichung</span>}
                        {(k.quelle === 'telegram' || (k.text || '').startsWith('[Telegram]')) && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-[#E3F2FD] text-[#1976D2]">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
                            Telegram
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[var(--text-secondary,#4B5563)] whitespace-pre-line">{k.text}</p>
                      {/* Inline media for linked comments */}
                      {k.medien_id && k.medium_dateipfad && (
                        <div className="mt-2">
                          {k.medium_dateityp === 'image' && (
                            <img
                              src={streamUrl(k.medium_dateipfad)}
                              alt={k.medium_dateiname || ''}
                              className="max-w-[400px] w-full rounded-lg border border-[var(--border-card,#E5E7EB)] cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => setLightboxImg(streamUrl(k.medium_dateipfad))}
                            />
                          )}
                          {k.medium_dateityp === 'video' && (
                            <video
                              src={streamUrl(k.medium_dateipfad)}
                              controls
                              preload="metadata"
                              className="max-w-[400px] w-full rounded-lg border border-[var(--border-card,#E5E7EB)]"
                            />
                          )}
                          {k.medium_dateityp === 'audio' && (
                            <div className="flex items-center gap-2 mt-1">
                              <Mic className="w-4 h-4 text-[#22D3EE] shrink-0" strokeWidth={1.5} />
                              <audio src={streamUrl(k.medium_dateipfad)} controls preload="metadata" className="max-w-[320px]" />
                            </div>
                          )}
                          {k.medium_beschreibung && (
                            <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1 italic">{k.medium_beschreibung}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Compose */}
              <div className="rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-3">
                <textarea
                  value={kommentarText}
                  onChange={e => setKommentarText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendKommentar(); }}
                  rows={3}
                  placeholder={t('prozesse.detail.commentPlaceholder') || 'Kommentar schreiben... (Ctrl+Enter zum Senden)'}
                  className="w-full px-2 py-1.5 text-sm text-[var(--text-primary,#1A1A2E)] bg-transparent resize-none focus:outline-none placeholder:text-[var(--text-tertiary,#6B7280)]"
                />
                <div className="flex justify-end mt-2">
                  <button onClick={sendKommentar} disabled={!kommentarText.trim() || sending}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-40 transition-colors">
                    <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('prozesse.detail.sendComment') || 'Kommentar senden'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* === VERSIONEN TAB === */}
          {activeTab === 'versionen' && (
            <div>
              {versionen.length === 0 ? (
                <div className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">
                  <History className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="text-sm">{t('prozesse.detail.noVersions') || 'Noch keine Versionen'}</p>
                </div>
              ) : (
                <div className="relative pl-8">
                  {/* Vertical line */}
                  <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-[var(--border-card,#E5E7EB)]" />
                  {versionen.map((v, idx) => (
                    <div key={v.id} className="relative mb-6 last:mb-0">
                      {/* Dot */}
                      <div className={`absolute -left-5 top-1 w-3 h-3 rounded-full border-2 ${idx === 0 ? 'bg-[#338edc] border-[#338edc]' : 'bg-[var(--bg-card,#fff)] border-[var(--border-card,#E5E7EB)]'}`} />
                      <div className="rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">v{v.version_label || v.version}</span>
                            {idx === 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#338edc]/10 text-[#338edc] font-medium">{t('prozesse.flowchart.current') || 'Aktuell'}</span>}
                          </div>
                          <button onClick={() => viewVersion(v.version)} className="flex items-center gap-1 text-xs text-[#338edc] hover:underline">
                            <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('prozesse.detail.viewVersion') || 'Version anzeigen'}
                          </button>
                        </div>
                        <p className="text-xs text-[var(--text-tertiary,#6B7280)]">
                          {formatDate(v.erstellt_am)} — {v.erstellt_von_name}
                        </p>
                        {v.aenderungsgrund && (
                          <p className="text-sm text-[var(--text-secondary,#4B5563)] mt-2 italic">"{v.aenderungsgrund}"</p>
                        )}
                        {/* AI Summary */}
                        {v.ai_zusammenfassung ? (
                          <div className="mt-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] p-3">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Sparkles className="w-3.5 h-3.5 text-[#338edc]" strokeWidth={1.5} />
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#338edc]">{t('prozesse.ai.summary') || 'AI-Zusammenfassung'}</span>
                            </div>
                            {(() => {
                              let parsed;
                              try { parsed = typeof v.ai_zusammenfassung === 'string' ? JSON.parse(v.ai_zusammenfassung) : v.ai_zusammenfassung; } catch { parsed = null; }
                              if (!parsed) return null;
                              return (
                                <>
                                  <p className="text-xs text-[var(--text-secondary,#4B5563)]">{parsed.zusammenfassung}</p>
                                  {(parsed.aenderungen || []).length > 0 && (
                                    <ul className="mt-1.5 space-y-0.5">
                                      {parsed.aenderungen.map((a, ai) => (
                                        <li key={ai} className="text-[11px] text-[var(--text-tertiary,#6B7280)] flex items-center gap-1">
                                          <span className={`w-1.5 h-1.5 rounded-full ${a.typ === 'hinzugefuegt' ? 'bg-emerald-500' : a.typ === 'entfernt' ? 'bg-red-400' : 'bg-amber-400'}`} />
                                          {a.details}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        ) : idx < versionen.length - 1 && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API}/api/prozesse/${id}/ai/changelog`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                                  body: JSON.stringify({ alte_version: versionen[idx + 1].version, neue_version: v.version }),
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  setVersionen(prev => prev.map(vv => vv.id === v.id ? { ...vv, ai_zusammenfassung: data } : vv));
                                }
                              } catch { /* ignore */ }
                            }}
                            className="mt-2 flex items-center gap-1 text-xs text-[#338edc] hover:underline"
                          >
                            <Sparkles className="w-3 h-3" strokeWidth={1.5} /> {t('prozesse.ai.generateSummary') || 'AI-Zusammenfassung generieren'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === LIFECYCLE / AUDIT-TRAIL TAB (#917) === */}
          {activeTab === 'lifecycle' && (
            <div>
              {/* Diff-Picker Toolbar */}
              {versionen.length >= 2 && (
                <div className="mb-4 rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4 flex flex-wrap items-center gap-3">
                  <GitCompare className="w-4 h-4 text-[#338edc]" strokeWidth={1.5} />
                  <span className="text-sm text-[var(--text-secondary,#4B5563)]">{t('prozesse.lifecycle.diffTitle') || 'Versionen vergleichen'}</span>
                  <select id="diff-vA" className="px-2 py-1 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)]">
                    {versionen.map(v => <option key={v.id} value={v.version}>v{v.version_label || v.version}</option>)}
                  </select>
                  <span className="text-[var(--text-tertiary,#6B7280)] text-sm">→</span>
                  <select id="diff-vB" className="px-2 py-1 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)]">
                    {versionen.map(v => <option key={v.id} value={v.version}>v{v.version_label || v.version}</option>)}
                  </select>
                  <button
                    onClick={() => {
                      const a = document.getElementById('diff-vA').value;
                      const b = document.getElementById('diff-vB').value;
                      if (a && b && a !== b) openDiff(a, b);
                    }}
                    className="ml-auto px-3 py-1.5 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] transition-colors"
                  >
                    {t('prozesse.lifecycle.diffShow') || 'Diff anzeigen'}
                  </button>
                </div>
              )}

              {/* Audit-Trail Liste */}
              {lifecycleLog.length === 0 ? (
                <div className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">
                  <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="text-sm">{t('prozesse.lifecycle.noEntries') || 'Noch keine Lifecycle-Einträge'}</p>
                </div>
              ) : (
                <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden divide-y divide-[var(--border-card,#E5E7EB)]">
                  {lifecycleLog.map(entry => {
                    const meta = LIFECYCLE_AKTION_META[entry.aktion] || { label: entry.aktion, icon: Activity, color: '#9CA3AF' };
                    const Icon = meta.icon;
                    const aktionLabel = t(meta.labelKey) || meta.label;
                    return (
                      <div key={entry.id} className="p-4 flex items-start gap-3">
                        <div className="rounded-lg p-2 shrink-0" style={{ backgroundColor: meta.color + '15' }}>
                          <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: meta.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{aktionLabel}</span>
                            {entry.alter_wert && entry.neuer_wert && (
                              <span className="text-xs text-[var(--text-tertiary,#6B7280)] font-mono">{entry.alter_wert} → {entry.neuer_wert}</span>
                            )}
                          </div>
                          {entry.details && (
                            <p className="text-sm text-[var(--text-secondary,#4B5563)]">{entry.details}</p>
                          )}
                          {entry.begruendung && (
                            <p className="text-sm text-[var(--text-secondary,#4B5563)] mt-1 italic">"{entry.begruendung}"</p>
                          )}
                          <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1">
                            {formatDate(entry.erstellt_am)} — {entry.benutzer_name || t('prozesse.lifecycle.systemUser') || 'System'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* === NOTIFICATIONS TAB (#918) === */}
          {activeTab === 'notifications' && (
            <div>
              {/* #1731: Zuweisungskreis-Editor (SSoT prozess_zuweisungen) */}
              <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5 mb-4">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">
                      {t('prozesse.kreis.title') || 'Zuweisungskreis'}
                    </h3>
                    <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5">
                      {t('prozesse.kreis.subtitle') || 'Der Kreis steuert Benachrichtigung UND Schulung/Test. Hinzufügen legt offene Schulungs-Zuweisungen für alle aktiven Tests an.'}
                    </p>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={openKreisAdd}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('prozesse.kreis.addButton') || 'MA hinzufügen'}
                    </button>
                  )}
                </div>
                {kreisMsg && (
                  <div className={`text-xs rounded-lg p-2.5 mt-2 ${kreisMsg.error
                    ? 'bg-rose-50 border border-rose-200 text-rose-800'
                    : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
                    {kreisMsg.error
                      ? `${t('prozesse.notify.error') || 'Fehler'}: ${kreisMsg.error}`
                      : kreisMsg.removed
                        ? `${kreisMsg.removed} ${t('prozesse.kreis.removedMsg') || 'entfernt'} · ${t('prozesse.kreis.offeneGeloescht') || 'offene Zuweisungen gelöscht'}: ${kreisMsg.offene_geloescht}`
                        : `${t('prozesse.kreis.addedMsg') || 'Hinzugefügt'}: ${kreisMsg.added} · ${t('prozesse.kreis.testRowsMsg') || 'Schulungs-Zuweisungen erstellt'}: ${kreisMsg.test_rows}`}
                  </div>
                )}
                {kreis.length === 0 ? (
                  <p className="text-xs text-[var(--text-tertiary,#6B7280)] bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 mt-3">
                    {t('prozesse.kreis.leer') || 'Noch keine Mitarbeiter im Kreis. Über „MA hinzufügen" aufnehmen — Benachrichtigung und Schulung laufen über diesen Kreis.'}
                  </p>
                ) : (
                  <div className="mt-3 divide-y divide-[var(--border-card,#E5E7EB)]">
                    {kreis.map(m => (
                      <div key={m.id} className="flex items-center justify-between py-2 text-sm">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-[var(--text-primary,#1A1A2E)]">{m.name}</span>
                          {m.abteilung_name && (
                            <span className="ml-2 text-xs text-[var(--text-tertiary,#6B7280)]">{m.abteilung_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          <span className="text-xs text-[var(--text-tertiary,#6B7280)]" title={[
                            m.has_portal ? 'Portal' : (t('prozesse.notify.noPortal') || 'kein Portal-Account'),
                            m.has_telegram ? 'Telegram' : (t('prozesse.notify.noTelegram') || 'kein Telegram'),
                            m.has_sms ? 'SMS' : (t('prozesse.notify.noSms') || 'keine Telefonnummer'),
                          ].join(' · ')}>
                            {m.has_telegram && '✈ '}{m.has_sms ? '📱' : '⚠'}
                          </span>
                          {m.tests_offen > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                              {m.tests_offen} {t('prozesse.kreis.offen') || 'offen'}
                            </span>
                          )}
                          {m.tests_bestanden > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                              {m.tests_bestanden} {t('prozesse.kreis.bestanden') || 'bestanden'}
                            </span>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => kreisRemove(m)}
                              className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-rose-600 transition-colors"
                              title={t('prozesse.kreis.removeTitle') || 'Aus Kreis entfernen'}
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* #1731: MA-hinzufügen-Modal */}
              {showKreisAdd && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                  <div className="bg-[var(--bg-card,#fff)] rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto border border-[var(--border-card,#E5E7EB)]">
                    <div className="p-5 border-b border-[var(--border-card,#E5E7EB)] flex items-center justify-between">
                      <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
                        {t('prozesse.kreis.addTitle') || 'Mitarbeiter zum Kreis hinzufügen'}
                      </h3>
                      <button onClick={() => setShowKreisAdd(false)} className="p-1.5 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)] transition-colors">
                        <XIcon className="w-5 h-5" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="p-5 space-y-3">
                      <input
                        value={kreisQuery}
                        onChange={e => setKreisQuery(e.target.value)}
                        placeholder={t('prozesse.kreis.searchMa') || 'MA suchen …'}
                        className="w-full px-3 py-2 rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]"
                      />
                      <div className="max-h-64 overflow-y-auto border border-[var(--border-card,#E5E7EB)] rounded-lg">
                        {kreisMas
                          .filter(m => !kreis.some(k => k.id === m.id))
                          .filter(m => {
                            if (!kreisQuery.trim()) return true;
                            const q = kreisQuery.toLowerCase();
                            return `${m.vorname || ''} ${m.nachname || ''}`.toLowerCase().includes(q)
                              || (m.kuerzel || '').toLowerCase().includes(q);
                          })
                          .slice(0, 100)
                          .map(m => {
                            const selected = kreisSelIds.includes(m.id);
                            return (
                              <label key={m.id} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => setKreisSelIds(prev => selected ? prev.filter(x => x !== m.id) : [...prev, m.id])}
                                />
                                <span className="text-[var(--text-primary,#1A1A2E)]">{m.vorname} {m.nachname}</span>
                                {m.kuerzel && <span className="text-xs text-[var(--text-tertiary,#6B7280)]">({m.kuerzel})</span>}
                              </label>
                            );
                          })}
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          onClick={() => setShowKreisAdd(false)}
                          className="px-3 py-1.5 rounded-lg text-sm text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
                        >
                          {t('common.cancel') || 'Abbrechen'}
                        </button>
                        <button
                          onClick={kreisAdd}
                          disabled={kreisBusy || !kreisSelIds.length}
                          className="px-4 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors disabled:opacity-50"
                        >
                          {kreisBusy ? (t('prozesse.notify.sending') || 'Sende …') : (t('prozesse.kreis.addConfirm') || 'Hinzufügen')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Send-Form */}
              {isAdmin && (
                <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5 mb-4">
                  <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3">
                    {t('prozesse.notify.sendTitle') || 'Mitarbeiter benachrichtigen'}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">
                        {t('prozesse.notify.eventType') || 'Event'}
                      </label>
                      <select
                        value={notifyEvent}
                        onChange={e => setNotifyEvent(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)]"
                      >
                        <option value="prozess.aktiv_neu">{t('prozesse.notify.eventAktivNeu') || 'Neuer Prozess aktiv'}</option>
                        <option value="prozess.major_freigabe">{t('prozesse.notify.eventMajorFreigabe') || 'Major-Update freigegeben'}</option>
                        <option value="prozess.entsperrt_review">{t('prozesse.notify.eventEntsperrtReview') || 'Entsperrt — Review'}</option>
                      </select>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs text-[var(--text-tertiary,#6B7280)]">
                          {t('prozesse.notify.empfaengerkreis') || 'Empfängerkreis (per Mitarbeiter)'}
                        </label>
                        {zuweisungskreis.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setNotifyMaIds(
                              notifyMaIds.length === zuweisungskreis.length ? [] : zuweisungskreis.map(m => m.id)
                            )}
                            className="text-xs text-[#338edc] hover:underline"
                          >
                            {notifyMaIds.length === zuweisungskreis.length
                              ? (t('prozesse.notify.deselectAll') || 'Keine')
                              : (t('prozesse.notify.selectAll') || 'Alle')}
                          </button>
                        )}
                      </div>
                      {zuweisungskreis.length === 0 ? (
                        <p className="text-xs text-[var(--text-tertiary,#6B7280)] bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3">
                          {t('prozesse.notify.kreisLeer') || 'Kein Empfängerkreis gesetzt. Füge oben im Zuweisungskreis Mitarbeiter hinzu.'}
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {zuweisungskreis.map(m => {
                            const checked = notifyMaIds.includes(m.id);
                            return (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => setNotifyMaIds(s => checked ? s.filter(x => x !== m.id) : [...s, m.id])}
                                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                                  checked
                                    ? 'bg-[#338edc] text-white border-[#338edc]'
                                    : 'bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] border-[var(--border-card,#E5E7EB)] hover:border-[#338edc]'
                                }`}
                                title={[
                                  m.has_portal ? 'Portal' : (t('prozesse.notify.noPortal') || 'kein Portal-Account'),
                                  m.has_telegram ? 'Telegram' : (t('prozesse.notify.noTelegram') || 'kein Telegram'),
                                  m.has_sms ? 'SMS' : (t('prozesse.notify.noSms') || 'keine Telefonnummer'),
                                ].join(' · ')}
                              >
                                {m.name}
                                {!m.has_portal && !m.has_telegram && !m.has_sms && ' ⚠'}
                                {m.has_telegram && ' ✈'}
                                {m.has_sms && ' 📱'}
                                {m.nummer_fehlt && !m.has_sms && ' ⚠'}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* #1718-Rework: HR-Flag „Nummer fehlt" — lückenlos, auch für MA die via
                          Portal/Telegram erreicht werden, damit HR die Nummer nachtragen kann. */}
                      {(() => {
                        const ohneNummer = zuweisungskreis.filter(m => m.nummer_fehlt || !m.has_sms);
                        if (!ohneNummer.length) return null;
                        return (
                          <div className="text-xs rounded-lg p-2.5 mt-2.5 bg-amber-50 border border-amber-200 text-amber-800">
                            <span className="font-medium">⚠ {t('prozesse.notify.nummerFehltTitel') || 'Nummer fehlt (HR nachtragen)'}:</span>{' '}
                            {ohneNummer.map(m => m.name).join(', ')}
                            <div className="mt-1 opacity-80">
                              {t('prozesse.notify.nummerFehltHint') || 'Diese Mitarbeiter erhalten KEINE SMS (Portal/Telegram weiterhin). HR bitte Stammdaten-Telefonnummer ergänzen.'}
                            </div>
                          </div>
                        );
                      })()}
                      {/* #1718-Rework: SMS unbedingt — kein opt-in mehr */}
                      <p className="text-[11px] text-[var(--text-tertiary,#9CA3AF)] mt-2">
                        {t('prozesse.notify.kanalHintUnbedingt') || 'Kanäle: Portal-Aufgabe + Telegram (✈) + SMS (📱, unbedingt an alle mit hinterlegter Nummer). Kein Versand vor Freigabe.'}
                      </p>
                    </div>
                    {!prozess.freigegeben_am && (
                      <div className="text-xs rounded-lg p-2.5 bg-amber-50 border border-amber-200 text-amber-800">
                        {t('prozesse.notify.freigabeGate') || 'Benachrichtigung erst nach Freigabe des Prozesses möglich.'}
                      </div>
                    )}
                    <button
                      onClick={sendNotification}
                      disabled={notifySending || notifyMaIds.length === 0 || !prozess.freigegeben_am}
                      className="px-4 py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] transition-colors disabled:opacity-50"
                    >
                      {notifySending
                        ? (t('prozesse.notify.sending') || 'Sende …')
                        : (t('prozesse.notify.sendButton') || 'Benachrichtigung senden')}
                    </button>
                    {notifyResult && (
                      <div className={`text-sm rounded-lg p-3 ${notifyResult.error
                        ? 'bg-rose-50 border border-rose-200 text-rose-800'
                        : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
                        {notifyResult.error ? (
                          <span>{t('prozesse.notify.error') || 'Fehler'}: {notifyResult.error === 'not_freigegeben'
                            ? (t('prozesse.notify.freigabeGate') || 'Benachrichtigung erst nach Freigabe möglich.')
                            : notifyResult.error}</span>
                        ) : (
                          <div>
                            <div>
                              {(t('prozesse.notify.resultSent') || 'Versendet:')} {notifyResult.sent}/{notifyResult.total}
                              {notifyResult.failed > 0 && <> · {(t('prozesse.notify.resultFailed') || 'Fehler:')} {notifyResult.failed}</>}
                              {notifyResult.skipped > 0 && <> · {(t('prozesse.notify.resultSkipped') || 'Übersprungen:')} {notifyResult.skipped}</>}
                            </div>
                            <div className="text-xs mt-1 opacity-80">
                              {(t('prozesse.notify.channelPortal') || 'Portal')}: {notifyResult.by_channel?.in_app || 0} ·
                              Telegram: {notifyResult.by_channel?.telegram || 0} ·
                              SMS: {notifyResult.by_channel?.sms || 0}
                            </div>
                            {notifyResult.no_channel?.length > 0 && (
                              <div className="text-xs mt-1 opacity-80">
                                {t('prozesse.notify.noChannelHint') || 'Übersprungen (kein zustellbarer Kanal):'} {notifyResult.no_channel.map(u => u.name).join(', ')}
                              </div>
                            )}
                            {notifyResult.nummer_fehlt?.length > 0 && (
                              <div className="text-xs mt-1.5 rounded p-2 bg-amber-50 border border-amber-200 text-amber-800">
                                ⚠ {t('prozesse.notify.nummerFehltTitel') || 'Nummer fehlt (HR nachtragen)'}: {notifyResult.nummer_fehlt.map(u => u.name).join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Audit-Liste */}
              <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
                <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3">
                  {t('prozesse.notify.historyTitle') || 'Versand-Historie'}
                </h3>
                {notifications.length === 0 ? (
                  <p className="text-sm text-[var(--text-tertiary,#6B7280)] text-center py-6">
                    {t('prozesse.notify.noHistory') || 'Noch keine Notifications versendet.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {notifications.map(n => (
                      <div key={n.id} className="flex items-center justify-between text-sm border-b border-[var(--border-card,#E5E7EB)] pb-2 last:border-0">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[var(--text-primary,#1A1A2E)] truncate">
                            {n.vorname} {n.nachname}
                          </div>
                          <div className="text-xs text-[var(--text-tertiary,#6B7280)]">
                            {n.event_type} · {n.channel_used || '—'} · {new Date(n.sent_at).toLocaleString('de-AT')}
                          </div>
                        </div>
                        <div className="ml-3 flex items-center gap-2 shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            n.status === 'sent' ? 'bg-emerald-50 text-emerald-700' :
                            n.status === 'failed' ? 'bg-rose-50 text-rose-700' :
                            'bg-amber-50 text-amber-700'
                          }`}>
                            {n.status}
                          </span>
                          {n.acknowledged_at && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700" title={new Date(n.acknowledged_at).toLocaleString('de-AT')}>
                              ✓ {t('prozesse.notify.acked') || 'gelesen'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === MC-TEST TAB (#919a) === */}
          {activeTab === 'mctest' && (
            <MCTestEditor prozessId={prozess.id} prozess={prozess} isAdmin={isAdmin} />
          )}
        </div>
      </div>

      {/* ================================================================ */}
      {/* RIGHT SIDE — 30% Sidebar */}
      {/* ================================================================ */}
      <div className="w-full lg:w-[360px] lg:shrink-0">
        <div className="space-y-4 sticky top-4">

          {/* Status Card */}
          <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3">{t('prozesse.detail.status') || 'Status'}</h3>
            <div className="mb-4">
              <StatusBadge status={prozess.status} large t={t} />
            </div>
            {editingDisabled && prozess.status !== 'archiviert' && (
              <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>{lockHint}</span>
              </div>
            )}
            <div className="space-y-2">
              {/* #1474: Änderungsentwurf — In Bearbeitung → Einreichen */}
              {isDraft && prozess.status === 'entwurf' && (
                <button onClick={() => { loadAdminUsers(); setEinreichenModal({ open: true, userId: prozess.genehmiger_id || '', bump_level: 'minor' }); }} className="w-full py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] transition-colors flex items-center justify-center gap-1.5">
                  <Send className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.aenderung.einreichen') || 'Zur Genehmigung einreichen'}
                </button>
              )}
              {/* #1474: Änderungsentwurf — In Review → Genehmigen / Ablehnen */}
              {isDraft && prozess.status === 'in_review' && isAdmin && (
                <div className="flex gap-2">
                  <button disabled={aenderungBusy} onClick={aenderungGenehmigen} className="flex-1 py-2 text-sm rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60">
                    <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.aenderung.genehmigen') || 'Genehmigen'}
                  </button>
                  <button
                    onClick={() => setConfirmDialog({
                      open: true, title: t('prozesse.aenderung.ablehnen') || 'Änderung ablehnen',
                      message: t('prozesse.aenderung.ablehnenMessage') || 'Bitte geben Sie einen Grund an:',
                      onConfirm: aenderungAblehnen,
                      children: true,
                    })}
                    className="flex-1 py-2 text-sm rounded-lg border border-red-300 text-red-600 font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <XIcon className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.reject') || 'Ablehnen'}
                  </button>
                </div>
              )}
              {!isDraft && prozess.status === 'entwurf' && (
                <button onClick={requestReview} className="w-full py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] transition-colors flex items-center justify-center gap-1.5">
                  <Eye className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.requestReview') || 'Review anfordern'}
                </button>
              )}
              {!isDraft && prozess.status === 'in_review' && isAdmin && (
                <div className="flex gap-2">
                  <button onClick={approveProzess} className="flex-1 py-2 text-sm rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.approve') || 'Freigeben'}
                  </button>
                  <button
                    onClick={() => setConfirmDialog({
                      open: true, title: t('prozesse.detail.reject') || 'Ablehnen',
                      message: t('prozesse.detail.rejectMessage') || 'Bitte geben Sie einen Grund an:',
                      onConfirm: rejectProzess,
                      children: true,
                    })}
                    className="flex-1 py-2 text-sm rounded-lg border border-red-300 text-red-600 font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <XIcon className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.reject') || 'Ablehnen'}
                  </button>
                </div>
              )}
              {/* #1474: Gesperrter Prozess → Änderungen vorschlagen (AK1) */}
              {!isDraft && prozess.status === 'aktiv' && (
                prozess.offener_entwurf_id ? (
                  <button
                    onClick={() => navigate(`/prozesse/${prozess.offener_entwurf_id}`)}
                    className="w-full py-2 text-sm rounded-lg bg-[#6366F1] text-white font-medium hover:bg-[#4f51d6] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <GitBranch className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.aenderung.entwurfOeffnen') || 'Offenen Änderungsentwurf öffnen'}
                  </button>
                ) : (
                  <button
                    disabled={aenderungBusy}
                    onClick={aenderungVorschlagen}
                    className="w-full py-2 text-sm rounded-lg bg-[#6366F1] text-white font-medium hover:bg-[#4f51d6] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
                  >
                    <GitBranch className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.aenderung.vorschlagen') || 'Änderungen vorschlagen'}
                  </button>
                )
              )}
              {prozess.status === 'aktiv' && isAdmin && (
                <>
                  <button
                    onClick={() => setReasonModal({
                      open: true,
                      action: 'entsperren',
                      title: t('prozesse.lifecycle.entsperrenTitle') || 'Prozess entsperren',
                      begruendung: '',
                    })}
                    className="w-full py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] font-medium hover:bg-[var(--bg-hover,#FAFAFE)] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Unlock className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.lifecycle.entsperren') || 'Entsperren'}
                  </button>
                  <button
                    onClick={() => setReasonModal({
                      open: true,
                      action: 'archivieren',
                      title: t('prozesse.detail.archive') || 'Archivieren',
                      begruendung: '',
                    })}
                    className="w-full py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] font-medium hover:bg-[var(--bg-hover,#FAFAFE)] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Archive className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.archive') || 'Archivieren'}
                  </button>
                </>
              )}
              {prozess.status === 'entsperrt' && isAdmin && (
                <>
                  <button
                    onClick={() => setVersionBumpModal({ open: true, type: 'patch', begruendung: '' })}
                    className="w-full py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <GitBranch className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.lifecycle.versionBump') || 'Version anheben'}
                  </button>
                  <button
                    onClick={() => setReasonModal({
                      open: true,
                      action: 'sperren-wieder',
                      title: t('prozesse.lifecycle.sperrenWiederTitle') || 'Wieder sperren',
                      begruendung: '',
                    })}
                    className="w-full py-2 text-sm rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Lock className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.lifecycle.sperrenWieder') || 'Wieder sperren'}
                  </button>
                  <button
                    onClick={() => setReasonModal({
                      open: true,
                      action: 'archivieren',
                      title: t('prozesse.detail.archive') || 'Archivieren',
                      begruendung: '',
                    })}
                    className="w-full py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] font-medium hover:bg-[var(--bg-hover,#FAFAFE)] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Archive className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.archive') || 'Archivieren'}
                  </button>
                </>
              )}
              {!isAdmin && (prozess.status === 'aktiv' || prozess.status === 'in_review' || prozess.status === 'entsperrt') && (
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] italic">{t('prozesse.lifecycle.adminOnly') || 'Nur Administratoren können diesen Prozess verwalten.'}</p>
              )}
            </div>

            {/* Genehmiger-Anzeige + Picker */}
            {isAdmin && prozess.status !== 'archiviert' && (
              <div className="mt-4 pt-4 border-t border-[var(--border-card,#E5E7EB)]">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{t('prozesse.lifecycle.genehmiger') || 'Genehmiger'}</span>
                  {prozess.genehmiger_id && (
                    <button
                      onClick={() => {
                        loadAdminUsers();
                        setGenehmigerModal({ open: true, userId: prozess.genehmiger_id || '', begruendung: '' });
                      }}
                      className="text-xs text-[#338edc] hover:underline flex items-center gap-1"
                    >
                      <Pencil className="w-3 h-3" strokeWidth={1.5} /> {t('prozesse.lifecycle.editGenehmiger') || 'Bearbeiten'}
                    </button>
                  )}
                </div>
                {prozess.genehmiger_id ? (
                  <p className="text-sm text-[var(--text-primary,#1A1A2E)] mt-1">
                    {adminUsers.find(u => u.id === prozess.genehmiger_id)?.vorname
                      ? `${adminUsers.find(u => u.id === prozess.genehmiger_id).vorname} ${adminUsers.find(u => u.id === prozess.genehmiger_id).nachname || ''}`.trim()
                      : `User #${prozess.genehmiger_id}`}
                  </p>
                ) : (
                  <button
                    onClick={() => {
                      loadAdminUsers();
                      setGenehmigerModal({ open: true, userId: '', begruendung: '' });
                    }}
                    className="mt-1 flex items-center gap-1 text-xs text-[#338edc] hover:underline"
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('prozesse.lifecycle.addGenehmiger') || 'Genehmiger zuweisen'}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* #1472 KPIs nicht aktiviert → schlanker Aktivierungs-Link statt leerer/irreführender Health-Box (AK9/AK10) */}
          {!kpisActive && (
            <div className="rounded-2xl border border-dashed border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
              <button
                onClick={() => { setShowKpiPanel(true); setActiveTab('kpis'); }}
                className="flex items-center gap-2 text-sm text-[#338edc] hover:underline w-full"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.activateKpis') || 'KPIs aktivieren'}
              </button>
              <p className="mt-1.5 text-xs text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.activateKpisHint') || 'Optional — Kennzahlen erst nach Aktivierung.'}</p>
            </div>
          )}

          {/* #1723 P1 Schritt 2: Health-Score-Card (Health/KPI/Abweichungen) entfernt — CUT */}

          {/* Details Card */}
          <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3">{t('prozesse.detail.details') || 'Details'}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.abteilung') || 'Abteilung'}</span>
                {prozess.abteilung_slug ? (
                  <Link to={`/prozesse/abteilungen/${prozess.abteilung_slug}`} className="text-[#338edc] hover:underline font-medium">{prozess.abteilung_name}</Link>
                ) : (
                  <span className="text-[var(--text-primary,#1A1A2E)]">{prozess.abteilung_name || '—'}</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.bereich') || 'Bereich'}</span>
                <span className="text-[var(--text-primary,#1A1A2E)]">{prozess.bereich_name || '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.typ') || 'Typ'}</span>
                <select
                  value={prozess.typ}
                  onChange={e => updateProzess({ typ: e.target.value })}
                  className="text-right text-sm bg-transparent text-[var(--text-primary,#1A1A2E)] border-none focus:outline-none cursor-pointer font-medium pr-0"
                >
                  <option value="standard">Standard</option>
                  <option value="kundenspezifisch">Kundenspezifisch</option>
                  <option value="ausnahme">Ausnahme</option>
                </select>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.risikoLevel') || 'Risiko-Level'}</span>
                {prozess.risiko_level ? (
                  <div className="flex items-center gap-1.5">
                    <select
                      value={prozess.risiko_level}
                      onChange={e => updateProzess({ risiko_level: e.target.value })}
                      className="text-right text-sm bg-transparent border-none focus:outline-none cursor-pointer font-medium pr-0"
                      style={{ color: RISIKO_COLORS[prozess.risiko_level] || '#1A1A2E' }}
                    >
                      <option value="low">Niedrig</option>
                      <option value="medium">Mittel</option>
                      <option value="high">Hoch</option>
                    </select>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/api/prozesse/${id}/ai/risk`, { method: 'POST', credentials: 'include' });
                          if (res.ok) {
                            const data = await res.json();
                            if (data.risiko_level) setProzess(prev => ({ ...prev, risiko_level: data.risiko_level }));
                          }
                        } catch { /* ignore */ }
                      }}
                      className="p-1 rounded hover:bg-[var(--bg-hover,#FAFAFE)]"
                      title={t('prozesse.ai.assessRisk') || 'AI-Risiko-Bewertung'}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[#338edc]" strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => updateProzess({ risiko_level: 'medium' })}
                    className="flex items-center gap-1 text-xs text-[#338edc] hover:underline"
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('prozesse.detail.addRisiko') || 'Risiko-Level festlegen'}
                  </button>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.verantwortlicher') || 'Verantwortlicher'}</span>
                <span className="text-[var(--text-primary,#1A1A2E)] font-medium">{prozess.verantwortlicher_name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.version') || 'Version'}</span>
                <span className="text-[var(--text-primary,#1A1A2E)] font-medium font-mono">v{buildVersionString(prozess)}</span>
              </div>
              <div className="border-t border-[var(--border-table,#F3F4F6)] my-2" />
              <div className="flex justify-between">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.erstellt') || 'Erstellt'}</span>
                <span className="text-[var(--text-primary,#1A1A2E)] text-right text-xs">
                  {formatDateShort(prozess.erstellt_am)}<br />
                  <span className="text-[var(--text-tertiary,#6B7280)]">{prozess.erstellt_von_name}</span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.aktualisiert') || 'Aktualisiert'}</span>
                <span className="text-[var(--text-primary,#1A1A2E)] text-xs">{formatDateShort(prozess.aktualisiert_am)}</span>
              </div>
              {prozess.freigegeben_am && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.freigegeben') || 'Freigegeben'}</span>
                  <span className="text-[var(--text-primary,#1A1A2E)] text-right text-xs">
                    {formatDateShort(prozess.freigegeben_am)}<br />
                    <span className="text-[var(--text-tertiary,#6B7280)]">{prozess.freigegeben_von_name}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Verknuepfungen Card */}
          <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3 flex items-center gap-1.5">
              <Link2 className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.verknuepfungen') || 'Verknuepfungen'}
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {verknuepfungen.map(v => (
                <span key={v.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] border border-[var(--border-card,#E5E7EB)]">
                  <span className="text-[10px] text-[var(--text-tertiary,#6B7280)] uppercase">{VERKN_TYP_LABELS[v.verknuepfung_typ] || v.verknuepfung_typ}</span>
                  {v.referenz_name}
                  <button onClick={() => deleteVerknuepfung(v.id)} className="ml-0.5 hover:text-red-500"><XIcon className="w-3 h-3" strokeWidth={2} /></button>
                </span>
              ))}
            </div>
            {addingVerkn ? (
              <div className="space-y-2">
                <select value={verknForm.verknuepfung_typ} onChange={e => setVerknForm({ ...verknForm, verknuepfung_typ: e.target.value })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-xs focus:outline-none focus:border-[#338edc]">
                  <option value="kunde">Kunde</option>
                  <option value="system">System</option>
                  <option value="prozess">Prozess</option>
                </select>
                <input value={verknForm.referenz_name} onChange={e => setVerknForm({ ...verknForm, referenz_name: e.target.value })} placeholder="Name"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-xs focus:outline-none focus:border-[#338edc]" />
                <div className="flex gap-2">
                  <button onClick={() => setAddingVerkn(false)} className="flex-1 py-1 text-xs rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)]">Abbrechen</button>
                  <button onClick={addVerknuepfung} disabled={!verknForm.referenz_name.trim()} className="flex-1 py-1 text-xs rounded-lg bg-[#338edc] text-white font-medium disabled:opacity-40">Hinzufuegen</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingVerkn(true)} className="flex items-center gap-1 text-xs text-[#338edc] hover:underline">
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('prozesse.detail.addLink') || 'Verknuepfung'}
              </button>
            )}
          </div>

          {/* Tags Card */}
          <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3 flex items-center gap-1.5">
              <Tag className="w-4 h-4" strokeWidth={1.5} /> Tags
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {(prozess.tags || []).map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#338edc]/10 text-[#338edc]">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="ml-0.5 hover:text-red-500"><XIcon className="w-3 h-3" strokeWidth={2} /></button>
                </span>
              ))}
              {(!prozess.tags || prozess.tags.length === 0) && (
                <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.noTags') || 'Keine Tags'}</p>
              )}
            </div>
            <div className="flex gap-2">
              <input
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addTag(); }}
                placeholder={t('prozesse.detail.addTag') || 'Neuer Tag...'}
                className="flex-1 px-2.5 py-1.5 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-xs focus:outline-none focus:border-[#338edc]"
              />
              <button onClick={addTag} disabled={!newTag.trim()} className="px-2.5 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium disabled:opacity-40">
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Changelog Card */}
          <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3 flex items-center gap-1.5">
              <History className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.detail.changelog') || 'Aenderungsprotokoll'}
            </h3>
            {changelog.length === 0 ? (
              <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{t('prozesse.detail.noChangelog') || 'Keine Eintraege'}</p>
            ) : (
              <>
                <div className="space-y-2">
                  {(showAllChangelog ? changelog : changelog.slice(0, 5)).map(entry => (
                    <div key={entry.id} className="text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-[var(--text-primary,#1A1A2E)]">{entry.aktion}</span>
                      </div>
                      <div className="text-[var(--text-tertiary,#6B7280)]">
                        {entry.benutzer_name} — {formatDate(entry.erstellt_am)}
                      </div>
                      {entry.details && <p className="text-[var(--text-tertiary,#6B7280)] mt-0.5 truncate">{typeof entry.details === 'string' ? entry.details : JSON.stringify(entry.details)}</p>}
                    </div>
                  ))}
                </div>
                {changelog.length > 5 && (
                  <button onClick={() => setShowAllChangelog(!showAllChangelog)} className="text-xs text-[#338edc] hover:underline mt-2">
                    {showAllChangelog ? (t('prozesse.detail.showLess') || 'Weniger anzeigen') : (t('prozesse.detail.showAll') || 'Alle anzeigen')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* DIALOGS */}
      {/* ================================================================ */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.children ? null : confirmDialog.message}
        onConfirm={confirmDialog.onConfirm || (() => setConfirmDialog({ open: false }))}
        onCancel={() => { setConfirmDialog({ open: false }); setRejectKommentar(''); }}
        confirmLabel={confirmDialog.title === (t('prozesse.detail.reject') || 'Ablehnen') ? 'Ablehnen' : undefined}
        confirmColor={confirmDialog.title === (t('prozesse.detail.reject') || 'Ablehnen') ? '#EF4444' : undefined}
      >
        {confirmDialog.children && (
          <div>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-2">{confirmDialog.message || 'Bitte geben Sie einen Grund an:'}</p>
            <textarea
              value={rejectKommentar}
              onChange={e => setRejectKommentar(e.target.value)}
              rows={3}
              placeholder="Grund der Ablehnung..."
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm resize-none focus:outline-none focus:border-[#338edc]"
            />
          </div>
        )}
      </ConfirmDialog>

      <VersionModal
        open={versionModal.open}
        onClose={() => setVersionModal({ open: false, data: null })}
        versionData={versionModal.data}
      />

      {/* === Reason-Modal (entsperren / sperren-wieder / archivieren) === */}
      {reasonModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setReasonModal({ open: false, action: null, title: '', begruendung: '' })}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">{reasonModal.title}</h3>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-3">
              {reasonModal.action === 'entsperren'
                ? (t('prozesse.lifecycle.entsperrenHint') || 'Begründung für das Entsperren — wird im Audit-Log festgehalten.')
                : reasonModal.action === 'sperren-wieder'
                ? (t('prozesse.lifecycle.sperrenWiederHint') || 'Optional: Begründung warum wieder gesperrt.')
                : (t('prozesse.lifecycle.archivierenHint') || 'Optional: Begründung für das Archivieren.')}
            </p>
            <textarea
              autoFocus
              value={reasonModal.begruendung}
              onChange={e => setReasonModal(prev => ({ ...prev, begruendung: e.target.value }))}
              rows={3}
              placeholder={reasonModal.action === 'entsperren' ? (t('prozesse.lifecycle.reasonRequiredPlaceholder') || 'Pflicht — z.B. Korrektur an Schritt 3') : (t('prozesse.lifecycle.reasonOptionalPlaceholder') || 'Optional')}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm resize-none focus:outline-none focus:border-[#338edc]"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setReasonModal({ open: false, action: null, title: '', begruendung: '' })}
                className="px-4 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]"
              >
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button
                onClick={() => {
                  if (reasonModal.action === 'entsperren') entsperrenProzess(reasonModal.begruendung);
                  else if (reasonModal.action === 'sperren-wieder') sperrenWiederProzess(reasonModal.begruendung);
                  else if (reasonModal.action === 'archivieren') archiveProzess(reasonModal.begruendung);
                }}
                disabled={reasonModal.action === 'entsperren' && !reasonModal.begruendung.trim()}
                className="px-4 py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.confirm') || 'Bestätigen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Version-Bump-Modal === */}
      {versionBumpModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setVersionBumpModal({ open: false, type: 'patch', begruendung: '' })}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">{t('prozesse.lifecycle.versionBumpTitle') || 'Version anheben'}</h3>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-3">
              {t('prozesse.lifecycle.versionBumpHint') || 'Aktuelle Version'}: <span className="font-mono font-medium">v{buildVersionString(prozess)}</span>
            </p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {['major', 'minor', 'patch'].map(typ => {
                const isActive = versionBumpModal.type === typ;
                const colors = { major: '#EF4444', minor: '#F59E0B', patch: '#10B981' };
                return (
                  <button
                    key={typ}
                    onClick={() => setVersionBumpModal(prev => ({ ...prev, type: typ }))}
                    className={`py-3 rounded-lg border text-sm font-medium transition-all ${isActive ? 'border-[#338edc] bg-[#338edc]/5 text-[var(--text-primary,#1A1A2E)]' : 'border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]'}`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <GitBranch className="w-4 h-4" strokeWidth={1.5} style={{ color: colors[typ] }} />
                      <span className="capitalize">{t(`prozesse.lifecycle.${typ}`) || typ}</span>
                      <span className="text-[10px] text-[var(--text-tertiary,#6B7280)] font-mono">
                        {(() => {
                          const a = prozess.aktuelle_version || 1, b = prozess.version_minor || 0, c = prozess.version_patch || 0;
                          if (typ === 'major') return `${a + 1}.0.0`;
                          if (typ === 'minor') return `${a}.${b + 1}.0`;
                          return `${a}.${b}.${c + 1}`;
                        })()}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <textarea
              autoFocus
              value={versionBumpModal.begruendung}
              onChange={e => setVersionBumpModal(prev => ({ ...prev, begruendung: e.target.value }))}
              rows={3}
              placeholder={t('prozesse.lifecycle.bumpReasonPlaceholder') || 'Pflicht: Was hat sich geändert?'}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm resize-none focus:outline-none focus:border-[#338edc]"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setVersionBumpModal({ open: false, type: 'patch', begruendung: '' })}
                className="px-4 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]"
              >
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button
                onClick={() => versionBump(versionBumpModal.type, versionBumpModal.begruendung)}
                disabled={!versionBumpModal.begruendung.trim()}
                className="px-4 py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('prozesse.lifecycle.bumpVersion') || 'Version anheben'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Genehmiger-Modal === */}
      {genehmigerModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setGenehmigerModal({ open: false, userId: '', begruendung: '' })}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">{t('prozesse.lifecycle.genehmigerTitle') || 'Genehmiger zuweisen'}</h3>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-3">{t('prozesse.lifecycle.genehmigerHint') || 'Optional — verantwortliche Person für die Freigabe.'}</p>
            <select
              value={genehmigerModal.userId}
              onChange={e => setGenehmigerModal(prev => ({ ...prev, userId: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc] mb-3"
            >
              <option value="">— {t('prozesse.lifecycle.noGenehmiger') || 'Kein Genehmiger'} —</option>
              {adminUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.vorname} {u.nachname} ({u.rolle})
                </option>
              ))}
            </select>
            <textarea
              value={genehmigerModal.begruendung}
              onChange={e => setGenehmigerModal(prev => ({ ...prev, begruendung: e.target.value }))}
              rows={2}
              placeholder={t('prozesse.lifecycle.reasonOptionalPlaceholder') || 'Optional'}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm resize-none focus:outline-none focus:border-[#338edc]"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setGenehmigerModal({ open: false, userId: '', begruendung: '' })}
                className="px-4 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]"
              >
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button
                onClick={() => setGenehmiger(genehmigerModal.userId ? parseInt(genehmigerModal.userId, 10) : null, genehmigerModal.begruendung)}
                className="px-4 py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4]"
              >
                {t('common.save') || 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #1474: Einreichen-Modal (Genehmiger + Bump-Stufe) */}
      {einreichenModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setEinreichenModal({ open: false, userId: '', bump_level: 'minor' })}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">{t('prozesse.aenderung.einreichenTitle') || 'Änderung zur Genehmigung einreichen'}</h3>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-4">{t('prozesse.aenderung.einreichenHint') || 'Wähle den Genehmiger und die Versionsstufe. Nach der Genehmigung ersetzt dieser Entwurf die freigegebene Fassung.'}</p>

            <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-1">{t('prozesse.lifecycle.genehmiger') || 'Genehmiger'}</label>
            <select
              value={einreichenModal.userId}
              onChange={e => setEinreichenModal(prev => ({ ...prev, userId: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc] mb-1"
            >
              <option value="">— {t('prozesse.aenderung.genehmigerWaehlen') || 'Genehmiger wählen'} —</option>
              {adminUsers.map(u => (
                <option key={u.id} value={u.id}>{u.vorname} {u.nachname} ({u.rolle})</option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-tertiary,#6B7280)] mb-4">{t('prozesse.aenderung.vierAugen') || '4-Augen-Prinzip empfohlen: idealerweise jemand anderes als der Einreicher.'}</p>

            <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-2">{t('prozesse.aenderung.versionsstufe') || 'Versionsstufe'}</label>
            <div className="space-y-2 mb-4">
              {[
                { v: 'minor', labelKey: 'prozesse.aenderung.bumpMinor', label: 'Minor — funktionale Erweiterung (z.B. 1.0 → 1.1)' },
                { v: 'major', labelKey: 'prozesse.aenderung.bumpMajor', label: 'Major — wesentliche Änderung (z.B. 1.x → 2.0)' },
              ].map(opt => (
                <label key={opt.v} className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${einreichenModal.bump_level === opt.v ? 'border-[#338edc] bg-[#338edc]/5' : 'border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-hover,#FAFAFE)]'}`}>
                  <input
                    type="radio"
                    name="bump_level"
                    checked={einreichenModal.bump_level === opt.v}
                    onChange={() => setEinreichenModal(prev => ({ ...prev, bump_level: opt.v }))}
                    className="mt-0.5 accent-[#338edc]"
                  />
                  <span className="text-sm text-[var(--text-primary,#1A1A2E)]">{t(opt.labelKey) || opt.label}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEinreichenModal({ open: false, userId: '', bump_level: 'minor' })}
                className="px-4 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]"
              >
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button
                disabled={aenderungBusy || !einreichenModal.userId}
                onClick={aenderungEinreichen}
                className="px-4 py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-60 flex items-center gap-1.5"
              >
                <Send className="w-4 h-4" strokeWidth={1.5} /> {t('prozesse.aenderung.einreichen') || 'Einreichen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === #1474 Stufe 2: Nach-Genehmigung-Dialog === */}
      {nachModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)]">
                {t('prozesse.aenderung.nachTitel') || 'Änderung genehmigt'}
                {nachModal.versionLabel ? <span className="ml-1.5 text-sm font-mono text-[var(--text-tertiary,#6B7280)]">v{nachModal.versionLabel}</span> : null}
              </h3>
            </div>
            <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-4">
              {(t('prozesse.aenderung.nachIntro') || 'Was soll für die Betroffenen ausgelöst werden?')}
            </p>

            {/* Betroffene-Übersicht (WER + WIE VIELE) */}
            <div className="rounded-lg border border-[var(--border-card,#E5E7EB)] p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">
                  {t('prozesse.aenderung.nachBetroffene') || 'Betroffene'}
                  {nachModal.abteilungName ? ` · ${nachModal.abteilungName}` : ''}
                </span>
                <span className="text-xs font-medium text-[var(--text-primary,#1A1A2E)]">{nachModal.selectedMa.length}/{nachModal.betroffene.length}</span>
              </div>
              {nachModal.betroffene.length === 0 ? (
                <p className="text-sm text-[var(--text-tertiary,#6B7280)] italic">{t('prozesse.aenderung.nachKeineBetroffene') || 'Keine Betroffenen (keine HR-Abteilung zugeordnet).'}</p>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {nachModal.betroffene.map(b => {
                    const checked = nachModal.selectedMa.includes(b.mitarbeiter_id);
                    return (
                      <label key={b.mitarbeiter_id} className="flex items-center gap-2 text-sm text-[var(--text-primary,#1A1A2E)] cursor-pointer">
                        <input type="checkbox" checked={checked} className="accent-[#338edc]"
                          onChange={() => setNachModal(prev => ({ ...prev, selectedMa: checked ? prev.selectedMa.filter(x => x !== b.mitarbeiter_id) : [...prev.selectedMa, b.mitarbeiter_id] }))} />
                        <span>{b.name}</span>
                        {!b.user_id && <span className="text-[10px] text-[var(--text-muted,#9CA3AF)]">{t('prozesse.aenderung.nachKeinPortal') || '(kein Portal)'}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* (1) Benachrichtigen */}
            <label className="flex items-start gap-2 rounded-lg border px-3 py-2 mb-2 cursor-pointer transition-colors border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-hover,#FAFAFE)]">
              <input type="checkbox" checked={nachModal.benachrichtigen} className="mt-0.5 accent-[#338edc]"
                onChange={() => setNachModal(prev => ({ ...prev, benachrichtigen: !prev.benachrichtigen }))} />
              <span className="text-sm text-[var(--text-primary,#1A1A2E)]">
                {t('prozesse.aenderung.nachBenachrichtigen') || 'Betroffene über die neue Version benachrichtigen'}
              </span>
            </label>

            {/* (2) Schulung/MC-Test Pflicht */}
            <label className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${nachModal.tests.length === 0 ? 'opacity-50' : 'border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-hover,#FAFAFE)]'}`}>
              <input type="checkbox" disabled={nachModal.tests.length === 0} checked={nachModal.schulungPflicht} className="mt-0.5 accent-[#338edc]"
                onChange={() => setNachModal(prev => ({ ...prev, schulungPflicht: !prev.schulungPflicht }))} />
              <span className="text-sm text-[var(--text-primary,#1A1A2E)]">
                {t('prozesse.aenderung.nachSchulungPflicht') || 'Schulung / MC-Test als Pflicht zuweisen'}
                {nachModal.tests.length === 0 && <span className="block text-[11px] text-[var(--text-muted,#9CA3AF)]">{t('prozesse.aenderung.nachKeinTest') || 'Kein aktiver Test vorhanden'}</span>}
              </span>
            </label>
            {nachModal.schulungPflicht && nachModal.tests.length > 0 && (
              <div className="mt-2 ml-6 space-y-1">
                {nachModal.tests.map(tst => {
                  const checked = nachModal.selectedTests.includes(tst.id);
                  return (
                    <label key={tst.id} className="flex items-center gap-2 text-sm text-[var(--text-secondary,#4B5563)] cursor-pointer">
                      <input type="checkbox" checked={checked} className="accent-[#338edc]"
                        onChange={() => setNachModal(prev => ({ ...prev, selectedTests: checked ? prev.selectedTests.filter(x => x !== tst.id) : [...prev.selectedTests, tst.id] }))} />
                      <span>{tst.titel}</span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <button
                disabled={nachModal.busy}
                onClick={() => nachGenehmigungSubmit(true)}
                className="px-4 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)] disabled:opacity-60"
              >
                {t('prozesse.aenderung.nachUeberspringen') || 'Überspringen'}
              </button>
              <button
                disabled={nachModal.busy || (!nachModal.benachrichtigen && !nachModal.schulungPflicht)}
                onClick={() => nachGenehmigungSubmit(false)}
                className="px-4 py-2 text-sm rounded-lg bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-60 flex items-center gap-1.5"
              >
                {nachModal.busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('prozesse.aenderung.nachAusloesen') || 'Auslösen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Diff-Modal === */}
      {diffModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDiffModal({ open: false, vA: null, vB: null, data: null, loading: false })}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] flex items-center gap-2">
                <GitCompare className="w-5 h-5 text-[#338edc]" strokeWidth={1.5} />
                {t('prozesse.lifecycle.diffTitle') || 'Versionen vergleichen'}
                <span className="text-sm font-mono text-[var(--text-tertiary,#6B7280)]">v{diffModal.vA} → v{diffModal.vB}</span>
              </h3>
              <button onClick={() => setDiffModal({ open: false, vA: null, vB: null, data: null, loading: false })} className="p-1 rounded hover:bg-[var(--bg-hover,#FAFAFE)]">
                <XIcon className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
              </button>
            </div>
            {diffModal.loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#338edc]" /></div>
            ) : diffModal.data ? (
              <div className="space-y-3">
                {Object.keys(diffModal.data.summary || {}).length === 0 ? (
                  <p className="text-sm text-[var(--text-tertiary,#6B7280)] italic text-center py-6">{t('prozesse.lifecycle.noDiff') || 'Keine Unterschiede gefunden.'}</p>
                ) : (
                  <>
                    {diffModal.data.summary.beschreibung && (
                      <div className="rounded-lg border border-[var(--border-card,#E5E7EB)] p-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-2">{t('prozesse.detail.beschreibung') || 'Beschreibung'}</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="rounded-lg bg-red-50 border border-red-200 p-2">
                            <div className="text-[10px] uppercase font-semibold text-red-700 mb-1">— v{diffModal.vA}</div>
                            <p className="text-red-900 whitespace-pre-line">{diffModal.data.summary.beschreibung.from || '—'}</p>
                          </div>
                          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2">
                            <div className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">+ v{diffModal.vB}</div>
                            <p className="text-emerald-900 whitespace-pre-line">{diffModal.data.summary.beschreibung.to || '—'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {(diffModal.data.summary.typ || diffModal.data.summary.risiko_level) && (
                      <div className="rounded-lg border border-[var(--border-card,#E5E7EB)] p-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-2">{t('prozesse.lifecycle.metadata') || 'Metadaten'}</h4>
                        <div className="space-y-1 text-sm">
                          {diffModal.data.summary.typ && (
                            <div className="flex items-center gap-2">
                              <span className="text-[var(--text-tertiary,#6B7280)] w-24">Typ:</span>
                              <span className="text-red-600 font-mono">{diffModal.data.summary.typ.from}</span>
                              <span>→</span>
                              <span className="text-emerald-600 font-mono">{diffModal.data.summary.typ.to}</span>
                            </div>
                          )}
                          {diffModal.data.summary.risiko_level && (
                            <div className="flex items-center gap-2">
                              <span className="text-[var(--text-tertiary,#6B7280)] w-24">Risiko:</span>
                              <span className="text-red-600 font-mono">{diffModal.data.summary.risiko_level.from}</span>
                              <span>→</span>
                              <span className="text-emerald-600 font-mono">{diffModal.data.summary.risiko_level.to}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {diffModal.data.summary.tags && (
                      <div className="rounded-lg border border-[var(--border-card,#E5E7EB)] p-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-2">Tags</h4>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div><span className="text-red-700">v{diffModal.vA}: </span><span className="font-mono">{(diffModal.data.summary.tags.from || []).join(', ') || '—'}</span></div>
                          <div><span className="text-emerald-700">v{diffModal.vB}: </span><span className="font-mono">{(diffModal.data.summary.tags.to || []).join(', ') || '—'}</span></div>
                        </div>
                      </div>
                    )}
                    {diffModal.data.summary.schritte && (
                      <div className="rounded-lg border border-[var(--border-card,#E5E7EB)] p-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-2">{t('prozesse.detail.tabSchritte') || 'Schritte'}</h4>
                        <div className="flex gap-3 text-sm">
                          <span className="text-emerald-700">+{diffModal.data.summary.schritte.added}</span>
                          <span className="text-red-600">−{diffModal.data.summary.schritte.removed}</span>
                          <span className="text-amber-700">~{diffModal.data.summary.schritte.changed}</span>
                        </div>
                      </div>
                    )}
                    {diffModal.data.summary.kpis && (
                      <div className="rounded-lg border border-[var(--border-card,#E5E7EB)] p-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-2">KPIs</h4>
                        <div className="flex gap-3 text-sm">
                          <span className="text-emerald-700">+{diffModal.data.summary.kpis.added}</span>
                          <span className="text-red-600">−{diffModal.data.summary.kpis.removed}</span>
                          <span className="text-amber-700">~{diffModal.data.summary.kpis.changed}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
